import * as Sentry from '@sentry/react';
import { getLockStatus } from 'api/v2/expenses';
import { getExchangeRates } from 'api/v2/revenues';
import { pauseRecurrence } from 'api/v3/expenses';
import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import Anchor from 'components/atoms/Anchor';
import { Button } from 'components/atoms/Button';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { FakeField, FieldConnector, FieldErrorManager } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import { getOptionsDict } from 'components/atoms/Form/Select/helpers';
import { Toggle } from 'components/atoms/Form/Toggle';
import Input from 'components/atoms/Inputs/GroupInput.component';
import PreFilledByAITooltip from 'components/atoms/PreFilledByAITooltip.component';
import RepeatIcon from 'components/atoms/SVG/RepeatIcon.component';
import { Tooltip as TooltipV2 } from 'components/atoms/TooltipV2';
import Amount from 'components/molecules/CommonCells/Amount.component';
import NumberField from 'components/molecules/Fields/Number/Number';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import { PaymentLinkSelect } from 'components/molecules/Fields/PaymentLink';
import SuggestedTransaction from 'components/molecules/Fields/PaymentLink/SuggestedTransaction.component';
import { TransactionOption2 } from 'components/molecules/Fields/PaymentLink/utils';
import PeriodHelper from 'components/molecules/Fields/Period/PeriodHelper.components';
import { createPeriodsOptions, dateToPeriod, periodToValue } from 'components/molecules/Fields/Period/utils';
import { Helper } from 'components/molecules/Helper';
import LinkedItemsProgress from 'components/molecules/LinkedItemsProgress';
import SegmentedToggle from 'components/molecules/SegmentedToggle.component';
import { ExpenseSubmitLockedByTaxes } from 'components/molecules/SubmitLockByTaxes.compopnent';
import AISuggestCategory from 'components/organisms/AISuggestCategory';
import TaxImpact from 'components/organisms/TaxImpact';
import StickyControlsContainer from 'components/StickyControlsContainer';
import { useExpenseAiMetadata } from 'contexts/AIMetadataProvider/useExpenseAiMetadata';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCanAccessAccountableBanking from 'hooks/Bank/useCanAccessAccountableBanking';
import useHasTransactions from 'hooks/Bank/useHasTransactions';
import useSuperQuery from 'hooks/useSuperQuery';
import type { ExpenseCategoriesType } from 'hooks/useExpensesCategories';
import useFeatures from 'hooks/useFeatures';
import useGetWorkingDaysAndDeductibleAmount from 'hooks/useGetWorkingDaysAndDeductibleAmount';
import { useHighlight } from 'hooks/useHighlight';
import { get, isEmpty, last } from 'lodash';
import type { FC, MouseEventHandler, ReactNode } from 'react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import type { FieldPath, SetValueConfig, UseFormReturn } from 'react-hook-form';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import { useQueryClient } from 'react-query';
import { AlertIdsEnum } from 'types/AlertIdsEnum';
import { ExpensesCacheKeys } from 'types/cacheKeys.types';
import type { IExpenseFormValuesV2, IExpenseV2, PartialIExpenseV2 } from 'types/expenses.types';
import {
  ExpenseFormItemNames,
  ExpenseFormNames,
  ExpensePaymentStatusType,
  ExpenseVATRegime,
  ReviewStatusEnum,
  TripType,
  TripVehicleType,
  type SupplierAsOption,
} from 'types/expenses.types';
import type { Currency, Nullable } from 'types/global.types';
import { CurrenciesEnum, type LockStatusTypeCatgoriesed, type VATReturnFrequency } from 'types/global.types';
import { TaxImpactDocumentType } from 'types/taxes.types';
import type { Transaction, TransactionAsOption } from 'types/transactions.types';
import type { IUser } from 'types/users.types';
import { AccountTypeEnum, VATTypeEnum } from 'types/users.types';
import type { VehiclesOption } from 'types/vehicle.types';
import type { IVehicle } from 'types/vehicles.types';
import { isAlertDismissed, markAlertAsDismissed } from 'utils/AlertsStoreManager';
import { logAmplitude } from 'utils/amplitude';
import * as ExpensesEvents from 'utils/amplitude/events/expenses.amplitude';
import { viewSuggestedChanges } from 'utils/applySuggestedChanges';
import { autoMobileBE, CATEGORY_SOCIAL_CONTRIBUTIONS, CATEGORY_TAXES_AND_INSURANCE_VAT_PAYMENT, SELF_EMPLOYED_CAR_PRIVATE_USE } from 'utils/categories';
import { beVehicleDeductibliltyDate, formResetOptions, isExpert, isWeb } from 'utils/constants';
import { currenciesList } from 'utils/currencies';
import formatMoney from 'utils/formatMoney';
import getCurrencyIconPath from 'utils/getCurrencyIconPath';
import { createSentryCustomErrorMsg } from 'utils/helpers';
import { ClockIconOutline, EditOutlineIcon, MinusIcon, PlusIcon, TrashIconOutline } from 'utils/icons';
import { BelgiumFlagIcon, CheckCircleIcon, GermanyFlagIcon, GlobeIcon, RowsOutlineIcon, XIconIcon } from 'utils/icons/figmaIcons';
import convertMarkdown from 'utils/markdown';
import { getAmountWithoutVAT, getAmountWithVAT, getVATAmountFromAmountInclVAT, round, truncateNumber } from 'utils/math';
import { ONE_SECOND } from 'utils/time';
import transactionToOption from 'utils/transactionToOption';
import useDeleteBasketItem from '../useDeleteBasketItem';
import useGetBasket from '../useGetBasket';
import useUpdateExpenseInBakset from '../useUpdateExpenseInBakset';
import AddExpenseToBasket from './AddExpenseToBasket.component';
import ExpenseVATCells from './ExpenseVATCells.component';
import { AmortizationPeriod } from './Fields/AmortizationPeriod';
import { getRecommendedAmortizationDurationInYears } from './Fields/AmortizationPeriod/helpers';
import CategoryPickerWithAI from './Fields/CategoriesPicker/CategoryPickerWithAI';
import useExpensesCategoriesPicker, { type CategoryChangeParams } from './Fields/CategoriesPicker/useExpensesCategoriesPicker';
import useGuessExpenseCategoryWithAI from './Fields/CategoriesPicker/useGuessExpenseCategoryWithAI';
import { PayWithQRCode } from './Fields/PayWithQRCode';
import useRecurringForm from './Fields/RecurringForm/useRecurringForm';
import SupplierName from './Fields/SupplierName';
import VATRegime from './Fields/VATRegime';
import { Vehicle } from './Fields/Vehicle';
import useFormMeta from './useFormMeta';
import {
  expenseAlternativeTransactionsOptions,
  getDefaultVATRate,
  getExpenseLinkedPaymentsAmount,
  getExpenseLinkedPaymentsOppositeDirectionTotal,
  getGuessReasons,
  getLatestTransactionsDate,
  getLockStatusBody,
  getUpdatedIncomeTaxDeductibility,
  getUpdatedMaxDeductibleVAT,
  getVehicleIncomeTaxDeductibilityPerYear,
  isAmbiguousSupplier,
  isCustomFormCategory,
  isScamySupplier,
  itemDefault,
  supplierNameToSearchValue,
  TaxSearchWarning,
  useVATRatesGetter,
} from './utils';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';
import UseState from 'components/atoms/UseState';
import FilesDownloadList from 'components/organisms/FilesDownloadList';

const markdownComponents: Components = {
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline break-word">
      {children}
    </a>
  ),
  p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
  li: ({ children }) => (
    <li className="py-1 flex gap-2">
      <span className="text-2xl leading-tight">&#8226;</span>
      {children}
    </li>
  ),
};

type FormProps = {
  formAPI: UseFormReturn;
  expense: PartialIExpenseV2;
  headerMeta?: ReactNode;
  footerMeta?: ReactNode;
  onSubmit: (values: IExpenseFormValuesV2) => any;
  disabled?: boolean;
  disabledFields?: FieldPath<IExpenseV2>[] | '*';
  notDisabledFields?: FieldPath<IExpenseV2>[];
  onEditVehicles?: () => void;
  onPauseRecurrence?: () => void;
  categories: ExpenseCategoriesType;
  isLocked?: boolean;
  allowDynamicTypeSwitching?: boolean;
  setBoundingBoxField?: (field: string | null) => void;
  onClose: () => void;
};

const today = dayjs();

const Form: FC<FormProps> = ({
  formAPI,
  expense,
  headerMeta,
  footerMeta,
  onSubmit,
  disabled,
  disabledFields,
  notDisabledFields,
  onEditVehicles,
  onPauseRecurrence,
  categories,
  isLocked,
  allowDynamicTypeSwitching = true,
  setBoundingBoxField,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const customer = useGetCurrentCustomerData().data as IUser;

  const {
    watch,
    getValues,
    setValue,
    formState: { isSubmitting, errors: formErrors, dirtyFields, isValid },
  } = formAPI;

  // please keep this error log, it can be useful for fast debugging

  const hasErrors = !isValid;

  const hasDirtyFields = !isEmpty(dirtyFields);

  const formValues = watch() as IExpenseFormValuesV2;

  console.log(formErrors);

  const highlightAPI = useHighlight();

  // should be called BEFORE changing the field value, since hookform can update its state with mutations (kind of)
  // and by the time we call getValues(name) it will be already changed and old value will equal the next one
  const highlightFieldIfNeeded = (name: string, nextValue: any) => {
    if (getValues(name) === nextValue) return;
    highlightAPI.highlight(name);
  };

  const itemsController = useFieldArray({
    name: 'items',
    control: formAPI.control,
  });

  const removeItems = (index?: number | number[]) => {
    itemsController.remove(index);
    formAPI.reset(getValues(), formResetOptions);
  };

  // TODO: fix this
  // const issueField = useHighlighTaxCheckField(expense);

  const { getFormMeta, getItemMeta, getSuggestedChanges } = useFormMeta();

  const formMeta = getFormMeta(formValues);

  useEffect(() => {
    viewSuggestedChanges(formValues, getSuggestedChanges(formValues), setValue, true);
  }, [formValues]);

  const documentVATReturnFrequency = getValues('user.VATReturnFrequency') as VATReturnFrequency;
  const { country: customerCountry } = customer as IUser;

  const defaultTaxPeriod = expense.taxPeriod;
  const deductibleTaxCategories: string[] = useFeatures('expenses.deductible_tax_categories');

  const defaultVATRate = useFeatures('expenses.default_vat_rate');
  const invoiceIBAN = expense.guessed_data?.supplier?.iban;
  const canPayWithQRCode =
    useFeatures('expenses.can_pay_with_qr_code') &&
    !!invoiceIBAN &&
    !formMeta.helpers.isCreditNote &&
    formMeta.helpers.isUnpaid &&
    formMeta.helpers.isEuroExpense;
  // const reverseChargeHelper = useFeatures('expenses.reverse_charge_helper');

  // const isDirty = (key: string) => {
  //   return !!get(dirtyFields, key);
  // };

  const importVATCategory = useFeatures('expenses.import_vat_category');
  // const importFeesCategory = useFeatures('expenses.import_fees_category');

  const AllItemsAmountInclVAT = (formValues.items || []).reduce((acc: number, item) => {
    return acc + (item.currencyAmount || 0);
  }, 0);

  const AllItemsVATAmounts = formValues.items.reduce((acc: number, item) => {
    if (item.category?.id === importVATCategory) return acc;
    return acc + getVATAmountFromAmountInclVAT(item.currencyAmount || 0, item.VATRate || 0);
  }, 0);

  const AllItemsAmountExclVAT = formValues.items.reduce((acc: number, item) => {
    const itemAmount = item.currencyAmount || 0;
    const itemVATRate = item.VATRate || 0;
    if (item.category?.id === importVATCategory) return acc;
    return acc + getAmountWithoutVAT(itemAmount, itemVATRate);
  }, 0);

  const presetsOptions = useMemo(
    () =>
      expenseAlternativeTransactionsOptions.map((option) => ({
        ...option,
        label: t(option.label),
      })),
    [t],
  );

  const periodOptions = useMemo(
    () => createPeriodsOptions(formValues.expenseDate, documentVATReturnFrequency),
    [formValues.expenseDate, documentVATReturnFrequency],
  );

  const updatePeriodBasedOnDate = (dateFieldName: ExpenseFormNames | ExpenseFormNames[]) => {
    const latestValues = formAPI.getValues() as IExpenseFormValuesV2;
    const dateObject = [dateFieldName]
      .flat()
      .map((name) => ({ name, value: get(latestValues, name) }))
      .find(({ value }) => !!value);
    const dateValue = dateObject?.value;
    const dateName = dateObject?.name;
    const meta = getFormMeta(latestValues);
    const hasPeriod = meta.fields.period.isApplicable;
    if (!hasPeriod || isFieldDisabled(ExpenseFormNames.period)) return;
    if (!dateValue) return;
    const _date = dayjs(dateValue);
    if (dateName === ExpenseFormNames.paymentDate) {
      if (meta.helpers.does10DaysRuleApplies) {
        const period = latestValues.period;
        if (period && period.year < _date.year()) return;
        const newPeriod = dateToPeriod(_date.subtract(1, 'year').endOf('year'), documentVATReturnFrequency);
        return setValue(ExpenseFormNames.period, newPeriod);
      }
    }
    const period = dateToPeriod(_date, documentVATReturnFrequency);
    return setValue(ExpenseFormNames.period, period);
  };

  const updatePaymentDateBasedOnDate = (date: Nullable<string>, onAfterUpdate?: () => void) => {
    const latestValues = formAPI.getValues() as IExpenseFormValuesV2;
    const hasPaymentDate = getFormMeta(latestValues).fields.paymentDate.isApplicable;
    if (!hasPaymentDate || isFieldDisabled(ExpenseFormNames.paymentDate)) return;
    if (!date) return;
    const paymentDate = dayjs(date).format('YYYY-MM-DD');
    setValue(ExpenseFormNames.paymentDate, paymentDate);
    onAfterUpdate?.();
  };

  const updateItemDeductability = (i: number) => {
    const values = formAPI.getValues() as IExpenseFormValuesV2;
    const item = formAPI.getValues(`items.${i}`) as IExpenseFormValuesV2['items'][number];
    const isVehcileFieldApplicable = getItemMeta(values, item, i).fields.vehicle.isApplicable;
    const maxKey = `items.${i}.maxDeductibleVAT`;
    const maxValue = getUpdatedMaxDeductibleVAT(item, isVehcileFieldApplicable);
    const incomeKey = `items.${i}.incomeTaxDeductibility`;
    const icomeValue = getUpdatedIncomeTaxDeductibility(values.expenseDate, item, isVehcileFieldApplicable);
    highlightFieldIfNeeded(maxKey, maxValue);
    highlightFieldIfNeeded(incomeKey, icomeValue);
    setValue(maxKey, maxValue);
    setValue(incomeKey, icomeValue);
  };

  // const suggestedTransactionError = formErrors.transactionsSuggestedIsActive?.message;

  const isReviewed = formValues.accountantReview?.reviewStatus === 'reviewed';

  const hasTransactions = !!useHasTransactions().data;

  const dateFormat = useFeatures('formats.date');

  const lockStatusParams = getLockStatusBody(formValues, customerCountry);
  const lockBytaxesStatusAPI = useSuperQuery<LockStatusTypeCatgoriesed>({
    queryKey: [ExpensesCacheKeys.TaxLock, useId(), lockStatusParams],
    queryFn: () => getLockStatus(lockStatusParams),
    cacheTime: 0,
    staleTime: 0,
    debounceTime: ONE_SECOND * 0.5,
    enabled: !isLocked,
  });

  const isFieldDisabled = useMemo(() => {
    const disabled: Record<string, boolean> = {};
    const notDisabled: Record<string, boolean> = {};
    if (Array.isArray(disabledFields)) {
      disabledFields.forEach((key) => (disabled[key] = true));
    }
    notDisabledFields?.forEach((key) => (notDisabled[key] = true));

    return (key: string) => {
      if (notDisabled[key]) return false;
      if (disabled[key] || disabledFields === '*') return true;
      return false;
    };
  }, [disabledFields, notDisabledFields]);

  const expenseYear = formValues.expenseDate ? dayjs(formValues.expenseDate).year() : undefined;

  const workingDaysDeductibleAmount = useGetWorkingDaysAndDeductibleAmount(expenseYear, formValues._id, formMeta.helpers.isGermanPrivateCar).data;

  const hasMultipleItems = formValues.items?.length > 1;

  const pickerController = useExpensesCategoriesPicker({
    defaultTaxPeriod,
    taxPeriod: formValues.taxPeriod,
    tracing: {
      onOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDCATEGORY,
      onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDCATEGORY,
    },
    searchBarPlaceholder: t('expense.category_picker.placeholder'),
    disabledCategories: {
      [SELF_EMPLOYED_CAR_PRIVATE_USE]: hasMultipleItems,
      [CATEGORY_SOCIAL_CONTRIBUTIONS]: hasMultipleItems,
      [CATEGORY_TAXES_AND_INSURANCE_VAT_PAYMENT]: hasMultipleItems,
      // [importVATCategory]: hasMultipleItems && !formMeta.helpers.isImportVATForm,
    },
    tooltips: {
      [SELF_EMPLOYED_CAR_PRIVATE_USE]: hasMultipleItems ? t('one_item_categories.tooltip') : null,
      [CATEGORY_SOCIAL_CONTRIBUTIONS]: hasMultipleItems ? t('one_item_categories.tooltip') : null,
      [CATEGORY_TAXES_AND_INSURANCE_VAT_PAYMENT]: hasMultipleItems ? t('one_item_categories.tooltip') : null,
      // [importVATCategory]: hasMultipleItems && !formMeta.helpers.isImportVATForm ? t('one_item_categories.tooltip') : null,
    },
    renderGridHeader: (searchTerm, onChange) => (
      <>
        <TaxSearchWarning searchTerm={searchTerm} />
        <AISuggestCategory
          searchValue={searchTerm}
          formValues={formValues}
          categoriesDict={pickerController.api.data?.categoriesDict || {}}
          onChooseCategory={(category) => {
            onChange?.(category);
            pickerController.api.close();
          }}
        />
      </>
    ),
    renderHeader: (category: any) => {
      const isVehicleCategory = category?.list?.some((c: any) => c.id === SELF_EMPLOYED_CAR_PRIVATE_USE);
      if (!isVehicleCategory) return null;
      return (
        <Alert
          type="default"
          title={
            <div className="flex justify-between">
              <p className="text-primary-700">{t('expenses.info_bubble.car_business_or_private.title')}</p>
              <Helper
                trigger={(open) => (
                  <Button asDefault className="text-taxes ml-auto" onClick={open}>
                    {t('learn_more')}
                  </Button>
                )}
              >
                <Helper.Title>{t('expenses.info_bubble.car_business_or_private.title')}</Helper.Title>
                <Helper.Paragraph className="whitespace-pre-line">{t('expenses.info_bubble.car_business_or_private.text')}</Helper.Paragraph>
              </Helper>
            </div>
          }
        />
      );
    },
  });

  const getTransactionsIds = () => {
    const transactions = (formAPI.getValues(ExpenseFormNames.transactions) as IExpenseFormValuesV2['payments']['transactions']) || [];
    return transactions.map((t) => t._id);
  };

  const guessCategoryWithAI = useGuessExpenseCategoryWithAI({
    filePath: expense.file?.path,
    supplierName: formValues.supplier?.name,
    transactions: getTransactionsIds(),
  });
  // const guessedCategories = guessCategoryWithAI?.query?.data;

  const VATRates = useVATRatesGetter()({ expenseDate: formValues.expenseDate });

  useExpenseAiMetadata(formValues);

  const updateRate = useCallback((_data: unknown) => {
    const data = _data as Record<Currency, number | undefined> | undefined;
    const currency: Currency = formAPI.getValues('currency');
    const rate = data?.[currency || CurrenciesEnum.EUR];
    if (rate == null) return;
    formAPI.setValue('currencyRate', rate, { shouldValidate: true });
  }, []);

  const [isCurrencyQueryEnabledInitially] = useState(() => {
    const currency = formValues.currency;
    const isEuro = currency === CurrenciesEnum.EUR;
    if (currency == null || isEuro) return false;
    return formValues.currencyRate == null;
  });

  const currencyRatesQuery = useSuperQuery({
    queryKey: ExpensesCacheKeys.ExpenseCurrencyRates,
    queryFn: (_, cancelToken) => getExchangeRates(formAPI.getValues('expenseDate'), { cancelToken }),
    enabled: isCurrencyQueryEnabledInitially,
    cacheTime: 0,
    staleTime: 0,
    onSuccess: updateRate,
    debounceTime: ONE_SECOND * 0.5,
  });

  useEffect(() => {
    const sub = formAPI.watch((_, fieldData) => {
      const currencyValue = formAPI.getValues('currency');
      const isEuro = currencyValue === CurrenciesEnum.EUR;

      // if no currency or euro, no need to fetch rates or update anything
      // the form meta logic will handle it
      if (!currencyValue || isEuro) {
        // reset to make sure future currency changes will trigger the query and not use a stale cache
        // since we handle the fetching manually
        return queryClient.setQueryData(ExpensesCacheKeys.ExpenseCurrencyRates, undefined);
      }

      const isExpenseDate = fieldData.name === 'expenseDate';
      if (isExpenseDate) {
        return currencyRatesQuery.refetch({ cancelRefetch: true });
      }

      const isCurrency = fieldData.name === 'currency';
      const isCurrencyRate = fieldData.name === 'currencyRate';

      const isCurrencyRateEmpty = formAPI.getValues('currencyRate') == null;

      if (isCurrency || (isCurrencyRate && isCurrencyRateEmpty)) {
        if (currencyRatesQuery.isFetching) return;
        const rates = currencyRatesQuery.data;
        if (rates) return updateRate(rates);
        return currencyRatesQuery.refetch({ cancelRefetch: true });
      }
    });
    return () => sub.unsubscribe();
  }, [currencyRatesQuery.refetch, currencyRatesQuery.data, currencyRatesQuery.isFetching, queryClient.setQueryData]);

  const onChangeVATRate = (index: number, newRate: number | null, options?: SetValueConfig) => {
    const item = formAPI.getValues(ExpenseFormNames.items + '.' + index) as IExpenseFormValuesV2['items'][number];
    const getName = (name: string) => ExpenseFormNames.items + '.' + index + '.' + name;
    const rate = item.VATRate || 0;
    const amount = item.currencyAmount || 0;
    setValue(getName(ExpenseFormItemNames.VATRate), newRate, options);
    // if (newRate == null) return;
    if (item.isExclVAT) {
      const amountExcl = getAmountWithoutVAT(amount, rate);
      setValue(getName(ExpenseFormItemNames.currencyAmount), getAmountWithVAT(amountExcl, newRate || 0), options);
    }
  };

  const onChangeVATRegime = (index: number, newRegime: ExpenseVATRegime | null, options?: SetValueConfig) => {
    setValue(ExpenseFormNames.items + '.' + index + '.' + ExpenseFormItemNames.VATRegime, newRegime, options);
  };

  const updateItemVATByCategory = (index: number) => {
    const item = formAPI.getValues(ExpenseFormNames.items + '.' + index) as IExpenseFormValuesV2['items'][number];

    if (item.VATRegime != null || item.VATRate != null) return;

    const defaultVATRate = getDefaultVATRate({
      isCustomerBE: formMeta.helpers.isCustomerBE,
      expenseDate: formValues.expenseDate,
      categorySuggestedVATRate: item.category?.suggestedVATRate,
    });

    if (defaultVATRate == null) return;

    const masterRegime = formMeta.helpers.masterVATRegime;

    if (defaultVATRate == 0) {
      onChangeVATRegime(index, ExpenseVATRegime.exempt_item);
      return onChangeVATRate(index, 0);
    }

    if (masterRegime && masterRegime !== ExpenseVATRegime.standard) return;
    onChangeVATRegime(index, ExpenseVATRegime.standard);
    return onChangeVATRate(index, defaultVATRate);
  };

  const onChangeAmount = (index: number, value: number | null) => {
    const item = formAPI.getValues(ExpenseFormNames.items + '.' + index) as IExpenseFormValuesV2['items'][number];

    const getName = (name: string) => {
      return ExpenseFormNames.items + '.' + index + '.' + name;
    };

    const onChange = (value: number | null) => {
      setValue(getName(ExpenseFormItemNames.currencyAmount), value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      updateItemVATByCategory(index);

      // we have some cross item validation in case of isImportVATForm
      // some errors could be stale, so we trigger the validation again
      if (formMeta.helpers.isImportVATForm) formAPI.trigger('items');
    };

    if (value == null) {
      return onChange(null);
    }

    const amount = round(Math.abs(value as number));

    const isCreditNote = value < 0;
    if (allowDynamicTypeSwitching) {
      setValue(ExpenseFormNames.isCreditNote, isCreditNote);
    }

    const maybeNewRate = formAPI.getValues(getName(ExpenseFormItemNames.VATRate)) || 0;

    if (item.isExclVAT) {
      return onChange(getAmountWithVAT(amount, maybeNewRate));
    }

    onChange(amount);
  };

  const itemsCategoryPickerOnChange = (index: number, category: any, params?: CategoryChangeParams) => {
    const { shouldUpdateItemVATRate = true, shouldUpdateItemDeductability = true, shouldUpdateItemProfessionalPart = true } = params || {};

    const currentValue = formAPI.getValues(`items.${index}.category`);

    if (
      // import vat is custom form, it can have other special items with import fees category
      // if the user changes the category to a non-import vat category, we need to remove the import fees items
      (currentValue?.id === importVATCategory && category?.id !== importVATCategory) ||
      // similar to above, but in case of a non-import vat category to import vat category
      // we will remove all items and add the import vat category
      (currentValue?.id !== importVATCategory && category?.id === importVATCategory)
    ) {
      // as a simple solution we remove all items and re-add a default one
      // then we let the rest of the function run to actually fill it with the new category
      // also assign the right vat rate, deductability, etc
      const currencyAmount = formAPI.getValues(`items.${index}.currencyAmount`);
      removeItems();
      index = 0;
      itemsController.append({
        ...itemDefault,
        currencyAmount,
      });
    }

    if (shouldUpdateItemVATRate) {
      if (currentValue?.id !== category?.id) {
        // reset the local VAT rate if the category changes
        // the form meta will clean it up if the field is not applicable
        if (typeof category?.suggestedVATRate === 'number') {
          setValue(`items.${index}.localVATRate`, category.suggestedVATRate);
        }
      }
    }

    setValue(`items.${index}.category`, category, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (shouldUpdateItemProfessionalPart) {
      const professionalPartValue = (() => {
        const recommended = category?.recommendedProfessionalPart;
        if (recommended != null) return recommended;
        if (formMeta.helpers.isCustomerNaturalPerson && autoMobileBE.includes(category.id)) return null;
        const maxProfessional = category?.maxProfessionalPart;
        if (maxProfessional != null) return maxProfessional;
        return 1;
      })();

      setValue('items.' + index + '.professionalPart', professionalPartValue, {
        shouldValidate: !!professionalPartValue,
      });
    }

    if (params?.taxPeriod) {
      setValue('taxPeriod', params?.taxPeriod);
    }

    if (params?.vehicle) {
      setValue('items.' + index + '.vehicle', params?.vehicle, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (shouldUpdateItemDeductability) {
      updateItemDeductability(index);
    }

    if (shouldUpdateItemVATRate) {
      updateItemVATByCategory(index);
    }

    updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
  };

  const [shouldShowSimpleCurrencySwitch, setShowSimpleCurrencySwitch] = useState(() => {
    const values = formAPI.getValues() as IExpenseFormValuesV2;
    const isInitEuro = values.currency === CurrenciesEnum.EUR;
    const initGuess = values.guessed_data?.currency;
    return !isInitEuro || (isInitEuro && !!initGuess && initGuess !== CurrenciesEnum.EUR);
  });

  const canAccessAccountableBanking = useCanAccessAccountableBanking();

  const basketQuery = useGetBasket();

  const basketExpense = basketQuery.data?.items?.find(
    (item) => (item?.isDraft && item._id === formValues._frontendId) || (item?.document?.id && item.document.id === expense._id),
  ) as any;

  const [isBasketAmountAlertDismissed, dismissBasketAmountAlert] = useState(false);

  const [isBasketLinkedPaymentsAlertDimissed, dismissBasketLinkedPaymentsAlert] = useState(false);

  const [isBasketCurrencyAlertDismissed, dismissBasketCurrencyAlert] = useState(false);

  const updateExpenseInBasketAPI = useUpdateExpenseInBakset();

  const deleteItemAPI = useDeleteBasketItem();

  const [shouldExpandLocalVATFields, setShouldExpandLocalVATFields] = useState(() => {
    return false;
  });

  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);

  const guessedData = useMemo(
    () =>
      getGuessReasons(formValues.guessed_data, {
        userCountry: customer.country,
      }),
    [formValues.guessed_data, customer.country],
  );

  const translateGuessReason = (reason?: string | null) => {
    if (reason == null) return null;
    return t(reason, {
      nationality: t('nationality.' + customer.country),
      supplierName: guessedData?.supplier.value,
    });
  };

  const [recurringFormContent, recurringFormApi] = useRecurringForm();

  const isRecurring = !!formValues.recurrence || !!formValues.create_recurrence;

  const nextRecurrenceDate = formValues.recurrence?.next_date || formValues.create_recurrence?.next_date;

  const [isRecurringTooltipSeen, setIsRecurringTooltipSeen] = useState(() => isAlertDismissed(AlertIdsEnum.makeRecurringTooltip) || isRecurring);
  const [isRecurringTooltipOpen, setIsRecurringTooltipOpen] = useState(!isRecurringTooltipSeen);

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  const showChatBtn = useMemo(() => (shouldShowTaxAdvisor ? { showTaxAdvisorBtn: true } : { showIntercomBtn: true }), [shouldShowTaxAdvisor]);

  return (
    <>
      <FormProvider {...formAPI}>
        {pickerController.api.content}
        {recurringFormContent}
        <form className="relative">
          <div className="bg-[#FCFBFE] px-6 py-10 border-b border-primary-100">
            {headerMeta}
            <div className="flex flex-col gap-6">
              <FieldConnector
                name={ExpenseFormNames.expenseDate}
                disabled={isFieldDisabled}
                label={formMeta.helpers.isGermanPrivateCar ? t('date') : t('expense.expenseDate')}
                loading={lockBytaxesStatusAPI.isLoading}
                setBoundingBoxField={setBoundingBoxField}
                hint={nextRecurrenceDate ? t('expense.recurring_hint', { nextDate: dayjs(nextRecurrenceDate).format(dateFormat) }) : undefined}
              >
                {({ field: { value, onChange, ...field } }) => {
                  const createRecurrenceData = formAPI.getValues(ExpenseFormNames.create_recurrence);
                  const savedRecurrenceData = formAPI.getValues(ExpenseFormNames.saved_recurrence);
                  return (
                    <div className="flex items-center gap-2">
                      <DatePicker
                        tracing={{
                          onCalendarOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDEXPENSEDATE,
                          onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDEXPENSEDATE,
                        }}
                        {...field}
                        aiTooltip={{
                          enabled: value != null && value === guessedData?.date.value,
                          description: guessedData?.date.reason,
                        }}
                        highlightID={field.name}
                        selected={value}
                        onChange={(date) => {
                          const newValue = date ? dayjs(date).format('YYYY-MM-DD') : null;
                          if (!newValue || newValue === value) return;
                          onChange(newValue);

                          if (formValues.create_recurrence) {
                            formAPI.setValue(ExpenseFormNames.create_recurrence, null);
                            logAmplitude(ExpensesEvents.WEBAPP_RECURRINGEXPENSE_OPENONDATECHANGE);
                            recurringFormApi.create({
                              originalExpenseIncludesFile: !!formValues.file?.path,
                              defaultValues: {
                                expense_date: newValue,
                              },
                              onDone: () => recurringFormApi.close(),
                              onSubmit: (data) => {
                                formAPI.setValue(ExpenseFormNames.create_recurrence, data);
                              },
                            });
                          }

                          formValues.items?.forEach((_, index) => updateItemDeductability(index));

                          const paymentDate = formAPI.getValues(ExpenseFormNames.paymentDate);

                          if (!paymentDate) return updatePeriodBasedOnDate(ExpenseFormNames.expenseDate);

                          if (paymentDate === value) {
                            updatePaymentDateBasedOnDate(newValue, () => {
                              updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
                            });
                          }
                        }}
                      />
                      <TooltipV2
                        content={
                          <span className="flex gap-2">
                            {isRecurring ? t('expense.recurring.view_saved.tooltip') : t('expense.recurring.create.title')}
                            {!isRecurringTooltipSeen && (
                              <Button
                                asDefault
                                onClick={() => {
                                  setIsRecurringTooltipOpen(false);
                                }}
                              >
                                <XIconIcon className="w-5 h-5" />
                              </Button>
                            )}
                          </span>
                        }
                        rootProps={{
                          open: isRecurringTooltipOpen,
                          onOpenChangeComplete: (open) => {
                            if (open || isRecurringTooltipSeen) return;
                            setIsRecurringTooltipSeen(true);
                            markAlertAsDismissed(AlertIdsEnum.makeRecurringTooltip);
                          },
                          onOpenChange: (open) => {
                            if (!isRecurringTooltipSeen && !open) return;
                            setIsRecurringTooltipOpen(open);
                          },
                        }}
                        positionProps={{
                          side: 'top',
                          align: 'start',
                        }}
                      >
                        <Button
                          structure="light"
                          size="xs"
                          iconOnly
                          className="relative"
                          disabled={field.disabled}
                          tracingEvent={ExpensesEvents.WEBAPP_RECURRINGEXPENSE_CLICKRECURRENCE}
                          onClick={() => {
                            if (createRecurrenceData) {
                              return recurringFormApi.edit({
                                originalExpenseIncludesFile: !!formValues.file?.path,
                                defaultValues: {
                                  ...formAPI.getValues(ExpenseFormNames.create_recurrence),
                                  expense_date: value,
                                },
                                onDone: () => recurringFormApi.close(),
                                onSubmit: (data) => {
                                  formAPI.setValue(ExpenseFormNames.create_recurrence, data);
                                },
                              });
                            }
                            if (savedRecurrenceData) {
                              return recurringFormApi.delete({
                                data: formAPI.getValues(ExpenseFormNames.saved_recurrence),
                                onSubmit: () => {
                                  return pauseRecurrence(savedRecurrenceData._id).then(() => {
                                    // alert
                                    recurringFormApi.close();
                                    formAPI.setValue(ExpenseFormNames.saved_recurrence, null);
                                    onPauseRecurrence?.();
                                  });
                                },
                              });
                            }
                            recurringFormApi.create({
                              originalExpenseIncludesFile: !!formValues.file?.path,
                              defaultValues: {
                                ...formAPI.getValues(ExpenseFormNames.create_recurrence),
                                expense_date: value,
                              },
                              onDone: () => recurringFormApi.close(),
                              onSubmit: (data) => {
                                formAPI.setValue(ExpenseFormNames.create_recurrence, data);
                              },
                            });
                          }}
                        >
                          {(createRecurrenceData || savedRecurrenceData) && (
                            <CheckCircleIcon className="w-6 h-6 text-[#059669] absolute top-0 right-0 translate-x-1/2 -translate-y-1/2" />
                          )}
                          <RepeatIcon className="w-5 h-5" viewBox="1 1 22 23" />
                        </Button>
                      </TooltipV2>
                    </div>
                  );
                }}
              </FieldConnector>
              {(formMeta.helpers.isCustomerBE || formMeta.fields.paymentDate.isHidden) && !!lockBytaxesStatusAPI.data?.date && (
                <ExpenseSubmitLockedByTaxes
                  isCreditNote={!!formValues.isCreditNote}
                  fieldSlug={'expense_date'}
                  taxLockReasons={lockBytaxesStatusAPI.data.date}
                />
              )}
              {formMeta.helpers.shouldShowExpenseDateWarning && (
                <Alert type="info" name="expense date in the future" {...showChatBtn} description={t('warning_bubble.document_date_in_the_future')} />
              )}
              {formMeta.helpers.shouldShowTenDayRuleWarning && (
                <Alert
                  type="info"
                  name="10 days rule"
                  className="mb-0"
                  description={
                    <div className="flex gap-4">
                      <span>
                        {t('expenses.warning_bubble.ten_days_rule.de', {
                          date: dayjs(formValues.expenseDate, 'YYYY-MM-DD').subtract(1, 'year').endOf('year').format(dateFormat),
                        })}
                      </span>
                      <span className="flex-shrink-0">
                        <Helper>
                          <Helper.Paragraph>{t('expenses.warning_bubble.ten_days_rule.helper.de')}</Helper.Paragraph>
                        </Helper>
                      </span>
                    </div>
                  }
                />
              )}

              {!formMeta.fields.supplier.isHidden && (
                <FieldConnector name={ExpenseFormNames.supplierName} label={t('expense.supplier.name')} setBoundingBoxField={setBoundingBoxField}>
                  {({ field: { onChange, ...field } }) => (
                    <SupplierName
                      {...field}
                      aiTooltip={{
                        enabled: field.value != null && field.value === guessedData?.supplier.value,
                        description: translateGuessReason(guessedData?.supplier.reason),
                      }}
                      placeholder={t('expense.supplier_field.placeholder')}
                      disabled={isFieldDisabled(field.name)}
                      tracing={{
                        onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDSUPPLIERNAME,
                        onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDSUPPLIERNAME,
                      }}
                      onChange={(option) => {
                        if (!option) return;
                        onChange(option.value);
                        if (option.__isCreated || formMeta.helpers.isCustomForm) return;
                        const supplierOption = option as SupplierAsOption;
                        const { categoryId, professionalPart } = supplierOption.supplier;

                        const withCategory = categoryId && categories.dict[categoryId];
                        if (!withCategory || isCustomFormCategory(categoryId)) return;

                        formValues.items?.forEach((_, index) => {
                          itemsCategoryPickerOnChange(index, withCategory, { shouldUpdateItemProfessionalPart: professionalPart == null });
                          if (professionalPart != null) {
                            formAPI.setValue(`${ExpenseFormNames.items}.${index}.${ExpenseFormItemNames.professionalPart}`, professionalPart, {
                              shouldValidate: true,
                            });
                          }
                        });
                      }}
                    />
                  )}
                </FieldConnector>
              )}

              {isScamySupplier(formValues.supplier?.name) && formMeta.helpers.isCustomerBE && (
                <Alert
                  type="warning"
                  name="scamy supplier"
                  className="mb-0"
                  description={t('expenses.warning_bubble.scamy_supplier', {
                    name: formValues.supplier?.name,
                  })}
                  renderButtons={(className) => (
                    <a
                      href={t('expenses.warning_bubble.scamy_supplier.link')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classNames(className, 'font-semibold underline text-sm')}
                    >
                      {t('read_blog_post')}
                    </a>
                  )}
                />
              )}

              {isAmbiguousSupplier(formValues.supplier?.name) && (
                <Alert type="info" className="mb-0" {...showChatBtn} name="ambiguous supplier" description={t('expenses.warning_bubble.ambiguous_supplier')} />
              )}

              {formMeta.helpers.shouldShowTaxSupplierWarningForCompany && (
                <Alert
                  type="warning"
                  className="mb-0"
                  name="Tax or VAT included in expense supplier name (company)"
                  {...showChatBtn}
                  description={t('expenses.warning_bubble.tax_supplier_warning.company')}
                />
              )}

              {formMeta.helpers.shouldShowTaxSupplierWarningForNaturalPerson && (
                <Alert
                  type="warning"
                  className="mb-0"
                  name="Tax or VAT included in expense supplier name (natural person)"
                  {...showChatBtn}
                  description={t('expenses.warning_bubble.tax_supplier_warning.natural')}
                />
              )}

              {formMeta.helpers.shouldShowVATRecoverabilityInfo && (
                <Alert
                  type="info"
                  className="mb-0"
                  name="DE VAT recoverability info"
                  title={t('expenses.warning.vat_not_recoverable.title.de')}
                  description={t('expenses.warning.vat_not_recoverable.text.de')}
                  renderButtons={(theme) => (
                    <Helper
                      trigger={(open) => (
                        <Button onClick={open} asDefault className={classNames('text-sm font-semibold', theme)}>
                          {t('learn_more')}
                        </Button>
                      )}
                    >
                      <Helper.Title>{t('expenses.warning.vat_not_recoverable.helper.q1.de')}</Helper.Title>
                      <Helper.Paragraph>
                        <ReactMarkdown components={markdownComponents}>{t('expenses.warning.vat_not_recoverable.helper.a1.de')}</ReactMarkdown>
                      </Helper.Paragraph>
                      <Helper.Title>{t('expenses.warning.vat_not_recoverable.helper.q2.de')}</Helper.Title>
                      <Helper.Paragraph>
                        <ReactMarkdown components={markdownComponents}>{t('expenses.warning.vat_not_recoverable.helper.a2.de')}</ReactMarkdown>
                      </Helper.Paragraph>
                      <Helper.Title>{t('expenses.warning.vat_not_recoverable.helper.q3.de')}</Helper.Title>
                      <Helper.Paragraph>
                        <ReactMarkdown components={markdownComponents}>{t('expenses.warning.vat_not_recoverable.helper.a3.de')}</ReactMarkdown>
                      </Helper.Paragraph>
                    </Helper>
                  )}
                />
              )}

              <FieldConnector
                name={ExpenseFormNames.isInvoice}
                disabled={isFieldDisabled}
                showUI={!formMeta.fields.isInvoice.isHidden}
                label={t('expense.proper_invoice.label')}
                setBoundingBoxField={setBoundingBoxField}
                helper={
                  <Helper>
                    <Helper.Title>{t('helper_invoice_title')}</Helper.Title>
                    <Helper.Paragraph>{t('helper_invoice_text1')}</Helper.Paragraph>
                    <Helper.Paragraph>{t('helper_invoice_text2')}</Helper.Paragraph>
                    <Helper.Paragraph>{t('helper_invoice_text3')}</Helper.Paragraph>
                    <Helper.SubTitle>{t('helper_invoice_subtitle')}</Helper.SubTitle>
                    <Helper.Paragraph className="whitespace-pre-line">{t('helper_invoice_text4')}</Helper.Paragraph>
                    <Helper.Paragraph>{t('helper_invoice_text5.temp')}</Helper.Paragraph>
                    <Helper.Paragraph>{t('helper_invoice_text6')}</Helper.Paragraph>
                  </Helper>
                }
              >
                {({ field }) => {
                  return (
                    <SegmentedToggle
                      value={field.value}
                      disabled={field.disabled}
                      options={[
                        { label: t('yes'), value: true },
                        { label: t('no'), value: false },
                      ]}
                      aiTooltip={{
                        enabled: field.value === guessedData?.is_invoice.value,
                        description: translateGuessReason(guessedData?.is_invoice.reason),
                      }}
                      tracing={{
                        onChange: {
                          name: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDISINVOICE,
                          data: (value: boolean) => ({ value }),
                        },
                      }}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                    />
                  );
                }}
              </FieldConnector>
              {formMeta.helpers.shouldShowIsInvoiceWarning && (
                <Alert
                  className="mb-0"
                  type="warning"
                  {...showChatBtn}
                  name="classified as invoice without VAT number on the document"
                  description={t('expenses.warning_bubble.invoice_without_vat_number.be')}
                />
              )}
              {formMeta.helpers.shouShowIsTicketWarning && (
                <Alert
                  className="mb-0"
                  type="warning"
                  {...showChatBtn}
                  name="classified as ticket with VAT number on the document"
                  description={
                    <div className="flex gap-4">
                      <span>{t('expenses.warning_bubble.ticket_with_vat_number.be')}</span>
                      <span className="flex-shrink-0">
                        <Helper>
                          <Helper.Title>{t('expenses.warning_bubble.ticket_with_vat_number.helper_title1.be')}</Helper.Title>
                          <Helper.Paragraph className="whitespace-pre-wrap">
                            {t('expenses.warning_bubble.ticket_with_vat_number.helper_text1.be')}
                          </Helper.Paragraph>
                          <Helper.Title>{t('expenses.warning_bubble.ticket_with_vat_number.helper_title2.be')}</Helper.Title>
                          <Helper.Paragraph>{t('expenses.warning_bubble.ticket_with_vat_number.helper_text2.be')}</Helper.Paragraph>
                        </Helper>
                      </span>
                    </div>
                  }
                />
              )}

              {formMeta.helpers.shouldShowImportVATTicketBEWarning && (
                <Alert
                  className="mb-0"
                  type="warning"
                  {...showChatBtn}
                  name="be import vat ticket"
                  description={t('expenses.warning_bubble.import_vat_ticket.be')}
                />
              )}
            </div>
          </div>

          <div className="px-6 mt-10">
            {itemsController.fields.map((field, index) => {
              const itemName = `${ExpenseFormNames.items}.${index}`;
              const item = getValues(itemName) as IExpenseFormValuesV2['items'][number];
              const itemMeta = getItemMeta(formValues, item, index);
              const getName = (name: string) => `${itemName}.${name}`;

              const guessedItem = guessedData?.items?.[index];

              const VATRateOptions = VATRates.map((rate) => {
                return {
                  value: rate,
                  label: `${Math.round(rate * 100)}%`,
                  icon: formMeta.helpers.isCustomerBE ? <BelgiumFlagIcon className="w-4 h-4" /> : <GermanyFlagIcon className="w-4 h-4" />,
                };
              });

              const priceLabel = (() => {
                if (formMeta.helpers.isImportVATForm) {
                  if (itemMeta.helpers.isImportVATItem) return t('import_vat_amount');
                  if (itemMeta.helpers.isImportFeesItem) return t('other_import_costs_amount');
                  return t('amountSign');
                }
                return t('amountSign');
              })();

              const toggleVATExcl = (isExclVAT: boolean) => {
                const rate = item.VATRate || 0;
                const amount = item.currencyAmount || 0;
                const newAmount = isExclVAT ? getAmountWithVAT(amount, rate) : getAmountWithoutVAT(amount, rate);
                setValue(getName('currencyAmount'), newAmount);
                setValue(getName('isExclVAT'), isExclVAT);
              };

              const latestCategoryVersion = item.category ? categories.dict[item.category.id] : null;
              const latestCategoryAllowVAT = latestCategoryVersion?.allowsVAT !== false;
              const savedCategoryAllowsVAT = item.category?.allowsVAT !== false;

              const categoryFieldName = getName(ExpenseFormItemNames.category);
              const shouldDisplayAllowsVATAlert = (() => {
                if (!latestCategoryVersion) return false;
                if (!formMeta.fields.isInvoice.value) return false;
                if (isFieldDisabled(categoryFieldName)) return false;
                if (latestCategoryAllowVAT === savedCategoryAllowsVAT) {
                  return false;
                }
                if (
                  savedCategoryAllowsVAT &&
                  // old VAT payments and german cars
                  itemMeta.fields.VATRate.isHidden
                ) {
                  // showing a warning about removing VAT while the VAT field is hidden will be weird
                  return false;
                }
                return true;
              })();

              const isImportVATItem = itemMeta.helpers.isImportVATItem;
              const isImportVATForm = formMeta.helpers.isImportVATForm;

              return (
                <>
                  {formValues.items?.length > 1 && !isImportVATItem && (
                    <div className="flex items-center justify-between mb-4">
                      <p className="font-bold text-main-900 text-xl tracking-tight font-avenir">{t('item', { number: isImportVATForm ? index : index + 1 })}</p>
                      {!isLocked && (
                        <Button onClick={() => removeItems(index)} structure="text" color="primary">
                          <TrashIconOutline className="w-5 text-primary-400" />
                        </Button>
                      )}
                    </div>
                  )}
                  <div key={field.id} className="flex flex-col gap-6 mb-6">
                    {!itemMeta.fields.category.isHidden && (
                      <FieldConnector
                        name={categoryFieldName}
                        disabled={() => isFieldDisabled(categoryFieldName) || itemMeta.fields.category.isDisabled}
                        label={t('expense.category')}
                        highlightID="categoryId"
                        labelExtra={
                          item.category &&
                          i18n.exists(`AYCD.${item.category.id}`) && (
                            <div className="ml-auto">
                              <Anchor
                                href={`${t(`AYCD.${item.category.id}`)}?utm_source=webapp&utm_medium=expenseform`}
                                target="_blank"
                                color="light"
                                rel="noopener noreferrer"
                                className="font-semibold text-sm"
                              >
                                {t('expense.category.can_i_deduct_it')}
                              </Anchor>
                            </div>
                          )
                        }
                      >
                        {({ field: { value, onChange, ...rest } }) => (
                          <>
                            <CategoryPickerWithAI
                              {...rest}
                              aiTooltip={{
                                enabled: guessedItem?.category.value != null && value?.id === guessedItem.category.value,
                                description: translateGuessReason(guessedItem?.category.reason),
                              }}
                              pickerController={pickerController}
                              placeholder={t('expense.category_field.placeholder')}
                              isDisabled={rest.disabled}
                              highlightID={rest.name}
                              value={value}
                              taxPeriod={formValues.taxPeriod}
                              onChange={(category, params) => itemsCategoryPickerOnChange(index, category, params)}
                              {...guessCategoryWithAI}
                            />
                          </>
                        )}
                      </FieldConnector>
                    )}
                    {itemMeta.helpers.shouldShowPrivateInsuranceWarning && (
                      <Alert type="info" {...showChatBtn} className="mb-0" description={t('expenses.info_bubble.private_insurance.text.de')} />
                    )}
                    {itemMeta.helpers.shouldShowVATDeductOnRentWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        className="mb-0 whitespace-pre-wrap"
                        name="be VAT deduction on rent"
                        title={t('expenses.info_bubble.vat_deduction_on_rent.title')}
                        description={t('expenses.info_bubble.vat_deduction_on_rent.text')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowImportVATCategoryInfo && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        className="mb-0"
                        description={<ReactMarkdown components={markdownComponents}>{t('expenses.info_bubble.import_vat.' + customerCountry)}</ReactMarkdown>}
                      />
                    )}
                    {itemMeta.helpers.shouldShowVehiclesMonthlyLeasingCategoryFieldWarning && (
                      <Alert
                        type="warning"
                        {...showChatBtn}
                        className="mb-0"
                        name="vehicles monthly leasing category field warning"
                        description={t('expenses.warning_bubble.monthly_leasing_category')}
                      />
                    )}
                    {shouldDisplayAllowsVATAlert && latestCategoryAllowVAT && !savedCategoryAllowsVAT && (
                      <Alert
                        type="error"
                        name="new category version allows VAT while the saved one does not"
                        // {...showChatBtn}
                        className="mb-0 VAT_CONFLICT_ERROR"
                        description={t('expense.warning_bubble.category_with_vat')}
                        renderButtons={(className) => (
                          <div className="text-right">
                            <Button
                              asDefault
                              className={classNames(className, 'text-sm font-semibold')}
                              onClick={() => {
                                itemsCategoryPickerOnChange(index, latestCategoryVersion);
                              }}
                            >
                              {t('add_vat')}
                            </Button>
                          </div>
                        )}
                      />
                    )}
                    {shouldDisplayAllowsVATAlert && !latestCategoryAllowVAT && savedCategoryAllowsVAT && (
                      <Alert
                        type="error"
                        name="new category version does not allow VAT while the saved one does"
                        // {...showChatBtn}
                        className="mb-0 VAT_CONFLICT_ERROR"
                        description={t('expense.warning_bubble.category_without_vat')}
                        renderButtons={(className) => (
                          <div className="text-right">
                            <Button
                              asDefault
                              className={classNames(className, 'text-sm font-semibold')}
                              onClick={() => {
                                itemsCategoryPickerOnChange(index, latestCategoryVersion);
                              }}
                            >
                              {t('remove_vat')}
                            </Button>
                          </div>
                        )}
                      />
                    )}
                    {itemMeta.helpers.shouldShowCompensationWarning && (
                      <Alert
                        type="warning"
                        {...showChatBtn}
                        className="mb-0"
                        name="compensation warning"
                        description={<ReactMarkdown components={markdownComponents}>{t('expenses.compensation_warning')}</ReactMarkdown>}
                      />
                    )}
                    {itemMeta.helpers.shouldShowLocalTransportWarning && (
                      <Alert
                        type="warning"
                        {...showChatBtn}
                        className="mb-0"
                        name="local transport warning"
                        description={t('expenses.public_transportation_warning', {
                          '613310b': categories.dict['613310b']?.title,
                        })}
                      />
                    )}
                    {itemMeta.helpers.shouldShowBusinessCarInfo && (
                      <Alert type="info" {...showChatBtn} className="mb-0" description={t('expenses.info_bubble.business_car_usage')} />
                    )}
                    {itemMeta.helpers.shouldShowUtilityVehicleWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        dismissable
                        dismissId={AlertIdsEnum.expenseUtilityVehicle}
                        className="mb-0"
                        name="vehicle must be classified as a utility"
                        description={
                          <div className="flex gap-4">
                            <span>{t('expenses.warning_bubble.utility_vehicle.be')}</span>
                            <span className="flex-shrink-0">
                              <Helper>
                                <Helper.Paragraph>{t('expenses.warning_bubble.utility_vehicle.helper.be')}</Helper.Paragraph>
                              </Helper>
                            </span>
                          </div>
                        }
                      />
                    )}
                    {itemMeta.helpers.shouldShowHealthWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        className="mb-0"
                        name="health insurance warning"
                        description={
                          <div className="flex gap-4">
                            <span>{t('expenses.warning_bubble.health_insurance.de')}</span>
                            <span className="flex-shrink-0">
                              <Helper>
                                <Helper.Paragraph>{t('expenses.warning_bubble.ten_days_rule.helper_text1.de')}</Helper.Paragraph>
                                <Helper.Paragraph>{t('expenses.warning_bubble.ten_days_rule.helper_text2.de')}</Helper.Paragraph>
                              </Helper>
                            </span>
                          </div>
                        }
                      />
                    )}
                    {itemMeta.helpers.shouldShowVehicleFineWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        className="mb-0"
                        name="a fixed fee for a parking violation can be classified as a parking expense"
                        description={
                          <div className="flex gap-4">
                            <span>{t('expenses.warning_bubble.vehicle_fine.be')}</span>
                            <span className="flex-shrink-0">
                              <Helper>
                                <Helper.Paragraph>{t('expenses.warning_bubble.vehicle_fine.helper.be')}</Helper.Paragraph>
                              </Helper>
                            </span>
                          </div>
                        }
                      />
                    )}
                    {itemMeta.helpers.shouldShowLunchWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        name="lunch voucher"
                        title={
                          <div className="flex gap-2">
                            <span>{t('attention')}</span>
                            <Helper>
                              <Helper.Title>{t('expenses.helper.lunch_voucher.title')}</Helper.Title>
                              <Helper.Paragraph>{t('expenses.helper.lunch_voucher.text')}</Helper.Paragraph>
                            </Helper>
                          </div>
                        }
                        description={t('expenses.lunch_voucher_warning')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowLumpsumWarning && (
                      <Alert type="info" {...showChatBtn} name="lumpsum warning" description={t('expenses.lumpsum_warning')} />
                    )}
                    {itemMeta.helpers.shouldShowSoftwareInvoiceWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        dismissable
                        dismissId={AlertIdsEnum.expenseSoftwareInvoice}
                        name="software invoice warning"
                        description={t('expenses.software_invoice_warning')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowSelfEmployedCompensationWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        name="self-employed compensation"
                        description={
                          <div className="flex gap-4">
                            <span>{t('expenses.self_employed_compensation_warning')}</span>
                            <span className="flex-shrink-0">
                              <Helper>
                                <Helper.Paragraph>{t('expenses.self_employed_compensation_warning.helper')}</Helper.Paragraph>
                              </Helper>
                            </span>
                          </div>
                        }
                      />
                    )}
                    {!itemMeta.fields.tripType.isHidden && (
                      <FieldConnector name={getName(ExpenseFormItemNames.tripType)} disabled={isFieldDisabled} label={t('expense.form.destination')}>
                        {({ field: { onChange, ...rest } }) => (
                          <Select
                            options={[
                              {
                                label: t('expense.form.destination.business_trip'),
                                value: TripType.businessTrip,
                              },
                              {
                                label: t('expense.form.destination.drive_to_work'),
                                value: TripType.driveToWork,
                              },
                            ]}
                            isSearchable={false}
                            onChange={(option: any) => {
                              onChange(option.value);
                            }}
                            {...rest}
                            isDisabled={rest.disabled}
                            tracing={{
                              onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDTRIPTYPE,
                              onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDTRIPTYPE,
                            }}
                          />
                        )}
                      </FieldConnector>
                    )}
                    {!itemMeta.fields.tripVehicleType.isHidden && (
                      <FieldConnector name={getName(ExpenseFormItemNames.tripVehicleType)} label={t('expense.form.type_of_vehicle')} disabled={isFieldDisabled}>
                        {({ field: { onChange, ...rest } }) => (
                          <Select
                            options={[
                              {
                                label: t('expense.form.type_of_vehicle.car'),
                                value: TripVehicleType.car,
                              },
                              {
                                label: t('expense.form.type_of_vehicle.motorbike'),
                                value: TripVehicleType.motorbike,
                              },
                            ]}
                            isSearchable={false}
                            onChange={(option: any) => {
                              onChange(option.value);
                            }}
                            {...rest}
                            isDisabled={rest.disabled}
                            tracing={{
                              onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDTRIPVEHICLETYPE,
                              onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDTRIPVEHICLETYPE,
                            }}
                          />
                        )}
                      </FieldConnector>
                    )}
                    {!itemMeta.fields.tripTotalKm.isHidden && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.tripTotalKm)}
                        label={itemMeta.fields.tripType.value === TripType.businessTrip ? t('expense.form.kilometers') : t('expense.form.distance')}
                        disabled={isFieldDisabled}
                      >
                        {({ field }) => (
                          <NumberField
                            {...field}
                            tracing={{
                              onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDTRIPTOTALKM,
                            }}
                          />
                        )}
                      </FieldConnector>
                    )}
                    {!itemMeta.fields.tripWorkingDays.isHidden && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.tripWorkingDays)}
                        label={t('expense.form.number_of_working_days')}
                        disabled={isFieldDisabled}
                      >
                        {({ field }) => (
                          <NumberField
                            {...field}
                            tracing={{
                              onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDTRIPTOTALDAYS,
                            }}
                          />
                        )}
                      </FieldConnector>
                    )}
                    {(() => {
                      if (itemMeta.fields.tripWorkingDays.value == null || workingDaysDeductibleAmount?.days == null) {
                        return null;
                      }
                      const totalWorkingDays = workingDaysDeductibleAmount.days + itemMeta.fields.tripWorkingDays.value;
                      const tProps = {
                        year: expenseYear,
                        totalDays: totalWorkingDays,
                      };
                      if (totalWorkingDays > 365) {
                        return (
                          <Alert
                            type="warning"
                            {...showChatBtn}
                            name="working days more than 365"
                            description={t('expenses.warning_bubble.working_days_more_than_365', tProps)}
                          />
                        );
                      }
                      if (totalWorkingDays > 250) {
                        return (
                          <Alert
                            type="warning"
                            {...showChatBtn}
                            name="working days more than 250"
                            description={t('expenses.warning_bubble.working_days_more_than_250', tProps)}
                          />
                        );
                      }
                      return null;
                    })()}
                    {!itemMeta.fields.deduction.isHidden && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.deduction)}
                        label={t('expense.form.deduction')}
                        disabled
                        helper={
                          <Helper>
                            {(itemMeta.helpers.shouldShowDeductionDestinationHelpers || itemMeta.helpers.shouldShowDeductionBusinessTripHelper) && (
                              <>
                                <Helper.Title>{t('expense.form.deduction.business_trip.helper_title')}</Helper.Title>
                                <Helper.Paragraph>
                                  {t('expense.form.deduction.business_trip.helper_text', {
                                    carAmount: formatMoney(0.3),
                                    motoAmount: formatMoney(0.2),
                                  })}
                                </Helper.Paragraph>

                                <Helper.Title>{t('expense.form.deduction.business_trip.helper_title2')}</Helper.Title>
                                <Helper.Paragraph>{t('expense.form.deduction.business_trip.helper_text2')}</Helper.Paragraph>
                              </>
                            )}
                            {(itemMeta.helpers.shouldShowDeductionDestinationHelpers || itemMeta.helpers.shouldShowDeductionDriveToWorkHelper) && (
                              <>
                                <Helper.Title>{t('expense.form.deduction.drive_to_office.helper_title')}</Helper.Title>
                                <Helper.Paragraph className="whitespace-pre-line">
                                  {t('expense.form.deduction.drive_to_office.helper_text.temp', {
                                    carAmount: formatMoney(0.38),
                                    maxDeductionAmount: formatMoney(4500),
                                  })}
                                </Helper.Paragraph>
                                <Helper.Title>{t('expense.form.deduction.drive_to_office.helper_title2')}</Helper.Title>
                                <Helper.Paragraph>{t('expense.form.deduction.drive_to_office.helper_text2')}</Helper.Paragraph>
                              </>
                            )}
                          </Helper>
                        }
                      >
                        {({ field }) => <PaymentAmount {...field} onChange={() => null} />}
                      </FieldConnector>
                    )}
                    {(() => {
                      if (
                        itemMeta.fields.deduction.value == null ||
                        workingDaysDeductibleAmount?.amount == null ||
                        itemMeta.fields.tripType.value !== TripType.driveToWork
                      ) {
                        return null;
                      }
                      const totalAmount = itemMeta.fields.deduction.value + workingDaysDeductibleAmount.amount;
                      if (totalAmount <= 4500) return null;
                      return (
                        <Alert
                          type="warning"
                          {...showChatBtn}
                          name="deduction more than 4500"
                          description={t('expenses.warning_bubble.deduction_more_than_4500', {
                            year: expenseYear,
                            limit: formatMoney(4500),
                            totalAmount: formatMoney(totalAmount),
                          })}
                        />
                      );
                    })()}
                    <FieldConnector
                      name={getName(ExpenseFormItemNames.vehicle)}
                      disabled={isFieldDisabled}
                      highlightID="select-vehicle"
                      showUI={!itemMeta.fields.vehicle.isHidden}
                      label={t('expense.vehicle')}
                      helper={
                        item.vehicle && formValues.expenseDate ? (
                          <Helper>
                            <Helper.Title>{t('expenses.helper.car_deductibility.title')}</Helper.Title>
                            <Helper.Paragraph>
                              <ul className="list-disc list-inside">
                                <li>
                                  {t('VAT')}
                                  {': '}
                                  <span className="text-primary-700">{`${item.vehicle?.VATDeductibility * 100}%`}</span>
                                </li>
                                <li>
                                  {t(
                                    customer?.accountType === AccountTypeEnum.one_person_company
                                      ? 'expenses.helper.car_deductibility.corporate_tax'
                                      : 'expenses.helper.car_deductibility.Income_tax',
                                  )}
                                  {': '}
                                  {(() => {
                                    const vehicle = item.vehicle as IVehicle;
                                    if (!vehicle.orderDate || dayjs(vehicle.orderDate + '', 'YYYYMMDD').isBefore(beVehicleDeductibliltyDate)) {
                                      return <span className="text-primary-700">{`${item.vehicle.incomeTaxDeductibility * 100}%`}</span>;
                                    }
                                    const startYear = Number((vehicle.orderDate + '').slice(0, 4));
                                    return (
                                      <ul className="list-[circle] list-inside mx-6">
                                        {Array.from({ length: 10 }).map((_, i) => (
                                          <li key={i}>
                                            {startYear + i}
                                            {': '}
                                            <span className="text-primary-700">{`${
                                              getVehicleIncomeTaxDeductibilityPerYear(vehicle, startYear + i) * 100
                                            }%`}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    );
                                  })()}
                                </li>
                              </ul>
                            </Helper.Paragraph>
                            <Helper.Paragraph>
                              <ReactMarkdown components={markdownComponents}>
                                {t('expenses.helper.car_deductibility.learn_more', {
                                  currentYear: today.year(),
                                  currentYearDeductibilityPercentage: getVehicleIncomeTaxDeductibilityPerYear(item.vehicle as IVehicle, today.year()) * 100,
                                })}
                              </ReactMarkdown>
                            </Helper.Paragraph>
                          </Helper>
                        ) : null
                      }
                    >
                      {({ field: { onChange, ...field } }) => {
                        const _onChange = (option: VehiclesOption | null) => {
                          onChange(option?.vehicle);
                          updateItemDeductability(index);
                        };
                        return (
                          <Vehicle
                            {...field}
                            value={field.value}
                            isDisabled={field.disabled}
                            onAddVehicle={(vehicle) => {
                              onEditVehicles?.();
                              _onChange(vehicle);
                            }}
                            onEditVehicle={(option) => {
                              onEditVehicles?.();
                              if (field.value?._id === option.value) {
                                _onChange(option);
                              }
                            }}
                            onDeleteVehicle={(option) => {
                              onEditVehicles?.();
                              if (field.value?._id === option.value) {
                                _onChange(null);
                              }
                            }}
                            onChange={(option) => _onChange(option as VehiclesOption)}
                          />
                        );
                      }}
                    </FieldConnector>
                    {!itemMeta.fields.currencyAmount.isHidden && (
                      <FakeField
                        className="flex-[2] min-w-0"
                        label={priceLabel}
                        labelExtra={
                          !itemMeta.fields.isExclVAT.isHidden ? (
                            <label htmlFor={getName(ExpenseFormItemNames.isExclVAT)} className="text-sm text-primary-400 ml-auto flex items-center gap-2">
                              <span>{t('expense.form.item_amount_toggle_excl_vat')}</span>
                              <Toggle
                                id={getName(ExpenseFormItemNames.isExclVAT)}
                                enabled={!!item.isExclVAT}
                                onChange={toggleVATExcl}
                                disabled={isFieldDisabled(getName(ExpenseFormItemNames.isExclVAT))}
                                size="xs"
                              />
                            </label>
                          ) : null
                        }
                        hint={
                          formValues.currency !== CurrenciesEnum.EUR && !!formValues.currencyRate && !!item.currencyAmount ? (
                            <span>
                              {`${t('expense.field.paymentAmount')} (${t('invoice.form.exchange_rate')} ${truncateNumber(formValues.currencyRate || 0, 3)}): `}
                              <span className="font-semibold">
                                {formatMoney((item.currencyAmount / formValues.currencyRate) * (formValues.isCreditNote ? -1 : 1))}
                              </span>
                            </span>
                          ) : null
                        }
                      >
                        {() => (
                          <>
                            <Input.Group>
                              {shouldShowSimpleCurrencySwitch && (
                                <Controller
                                  name={ExpenseFormNames.currency}
                                  render={({ field: { ref, ...fieldRest }, fieldState }) => {
                                    const iconPath = getCurrencyIconPath(fieldRest.value);
                                    return (
                                      <Input.Dropdown
                                        aiTooltip={{
                                          enabled: guessedData?.currency.value != null && fieldRest.value === guessedData.currency.value,
                                          description: translateGuessReason(guessedData?.currency.reason),
                                        }}
                                        wrapperClassName={classNames('w-3/12 min-w-[6.75rem]', fieldState.error ? 'field-with-error' : '')}
                                        removeRightRounding
                                        tracing={{
                                          onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDITEMCURRENCY,
                                          onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDITEMCURRENCY,
                                        }}
                                        isSearchable
                                        placeholder={t('search') + '...'}
                                        options={currenciesList.map((option) => ({
                                          ...option,
                                          label: t(option.slug),
                                        }))}
                                        controlShouldRenderValue={false}
                                        onMouseEnter={() => setBoundingBoxField?.(fieldRest.name)}
                                        onMouseLeave={() => setBoundingBoxField?.(null)}
                                        formatOptionLabel={(option, meta) => {
                                          if (meta.context === 'value') {
                                            return;
                                          }
                                          const iconPath = getCurrencyIconPath(option.value);
                                          return (
                                            <div className="flex items-center gap-2">
                                              {!!iconPath && <img src={iconPath} className="w-6 h-6" />}
                                              {option.label}
                                            </div>
                                          );
                                        }}
                                        {...fieldRest}
                                        onChange={(option: any) => {
                                          const newValue = option.value;
                                          if (newValue === fieldRest.value) {
                                            return;
                                          }
                                          fieldRest.onChange(newValue);
                                        }}
                                        isDisabled={isFieldDisabled(fieldRest.name)}
                                      >
                                        <div className="flex items-center gap-2">
                                          {!!iconPath && <img src={iconPath} className="w-6 h-6" />}
                                          {fieldRest.value}
                                        </div>
                                      </Input.Dropdown>
                                    );
                                  }}
                                />
                              )}

                              <Controller
                                name={getName(ExpenseFormItemNames.currencyAmount)}
                                render={({ field: { ref, ...fieldRest }, fieldState }) => {
                                  const value = fieldRest.value;
                                  const sign = formValues.isCreditNote ? -1 : 1;

                                  const adaptedValue = (() => {
                                    if (value == null) return null;
                                    return (item.isExclVAT ? getAmountWithoutVAT(value, item.VATRate || 0) : value) * sign;
                                  })();

                                  const guessedAmount =
                                    guessedItem?.net_amount.value != null && guessedItem?.vat_amount.value != null
                                      ? round(guessedItem.net_amount.value + guessedItem.vat_amount.value)
                                      : null;

                                  return (
                                    <Input.Number
                                      aiTooltip={{
                                        enabled: guessedAmount != null && value === guessedAmount,
                                      }}
                                      error={!!fieldState.error}
                                      highlightID={`amount.${item.currencyAmount}`}
                                      onMouseEnter={() => setBoundingBoxField?.(fieldRest.name)}
                                      onMouseLeave={() => setBoundingBoxField?.(null)}
                                      removeLeftRounding={shouldShowSimpleCurrencySwitch}
                                      wrapperClassName={classNames('flex-1 ml-[-1px]', fieldState.error ? 'field-with-error' : '')}
                                      tracing={{
                                        onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDAMOUNT,
                                      }}
                                      {...fieldRest}
                                      disabled={isFieldDisabled(fieldRest.name)}
                                      format={{
                                        hideCurrency: shouldShowSimpleCurrencySwitch,
                                        currency: formValues.currency,
                                      }}
                                      placeholder={t('expense.amount_field.placeholder')}
                                      value={adaptedValue}
                                      onChange={(value) => onChangeAmount(index, value)}
                                      keepZeroOnFocus={formMeta.helpers.isImportVATForm}
                                      renderExtraRight={(className) =>
                                        !!item.currencyAmount &&
                                        !!item.VATRate && (
                                          <span className={classNames('text-primary-400 flex items-center', className)}>
                                            {t('VAT')}
                                            {': '}
                                            {formatMoney(getVATAmountFromAmountInclVAT(item.currencyAmount, item.VATRate) * sign, formValues.currency)}
                                          </span>
                                        )
                                      }
                                    />
                                  );
                                }}
                              />
                            </Input.Group>
                            <FieldErrorManager className="mt-1.5" name={getName(ExpenseFormItemNames.currencyAmount)} />
                          </>
                        )}
                      </FakeField>
                    )}
                    {itemMeta.helpers.shouldShowCompanyMealVoucherWarning && (
                      <Alert
                        className="mb-0"
                        name="company meal voucher"
                        type="warning"
                        {...showChatBtn}
                        description={t('expenses.warning_bubble.meal_voucher_wrong_amount.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowImportVATAmountInfo && (
                      <Alert type="info" {...showChatBtn} className="mb-0" description={t('expenses.info_bubble.import_vat_amount.' + customerCountry)} />
                    )}
                    {!itemMeta.fields.VATRate.isHidden && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.VATRate)}
                        label={t('expense.vat_rate.field.label')}
                        className="flex-1"
                        disabled={(name) => isFieldDisabled(name) || itemMeta.fields.VATRate.isDisabled}
                        setBoundingBoxField={setBoundingBoxField}
                        helper={
                          <Helper>
                            <Helper.Title>{t('expenses.helper.VAT_rate.field.title')}</Helper.Title>
                            <Helper.Paragraph className="whitespace-pre-wrap">{t('expenses.helper.VAT_rate.field.desc')}</Helper.Paragraph>
                          </Helper>
                        }
                      >
                        {({ field }) => {
                          const fieldValue =
                            field.value == null && item.VATRegime === ExpenseVATRegime.foreign_vat ? ExpenseVATRegime.foreign_vat : field.value;
                          const options = [
                            ...VATRateOptions,
                            {
                              label: t('expense.form.foreign_vat'),
                              value: ExpenseVATRegime.foreign_vat,
                              icon: <GlobeIcon className="w-4 h-4" />,
                            },
                          ];
                          const isValueSuggestedByAI =
                            fieldValue != null &&
                            ((fieldValue === guessedItem?.vat_rate.value && guessedItem?.vat_regime.value !== ExpenseVATRegime.foreign_vat) ||
                              fieldValue === guessedItem?.vat_regime.value);
                          return (
                            <div className="flex flex-wrap gap-1.5">
                              {options.map((option) => {
                                const isSelected = fieldValue === option.value;
                                return (
                                  <PreFilledByAITooltip key={option.value} align="center" enabled={isValueSuggestedByAI && isSelected}>
                                    <Button
                                      asDefault
                                      disabled={field.disabled}
                                      tracingEvent={
                                        isSelected ? undefined : { name: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDVATRATE, data: { value: option.value } }
                                      }
                                      className={classNames('border rounded-full py-0.5 px-2 disabled:cursor-not-allowed disabled:bg-primary-50', {
                                        'bg-expenses-100 text-expenses': isSelected,
                                        'ai-button-bg-border': isSelected && isValueSuggestedByAI && !field.disabled,
                                        'border-expenses': isSelected && field.disabled,
                                        'border-transparent': isSelected && !field.disabled,
                                      })}
                                      onClick={() => {
                                        if (option.value === fieldValue) return;
                                        const itemIndex = index;
                                        if (option.value === ExpenseVATRegime.foreign_vat) {
                                          return formValues.items.forEach((item, index) => {
                                            if (index !== itemIndex) {
                                              if (item.VATRegime == null) return;
                                              if (formMeta.helpers.mixableVATRegimes.includes(item.VATRegime)) return;
                                            }
                                            onChangeVATRegime(index, ExpenseVATRegime.foreign_vat, { shouldValidate: true });
                                            onChangeVATRate(index, null, { shouldValidate: true });
                                          });
                                        }
                                        if (option.value === 0) {
                                          onChangeVATRegime(index, null);
                                          return onChangeVATRate(index, 0, { shouldValidate: true });
                                        }
                                        // standard vat rate
                                        return formValues.items.forEach((item, index) => {
                                          if (index !== itemIndex) {
                                            if (item.VATRegime == null) return;
                                            if (formMeta.helpers.mixableVATRegimes.includes(item.VATRegime)) return;
                                            if (item.VATRegime === ExpenseVATRegime.standard) return;
                                          }
                                          onChangeVATRegime(index, ExpenseVATRegime.standard, { shouldValidate: true });
                                          onChangeVATRate(index, option.value as number, { shouldValidate: true });
                                        });
                                      }}
                                    >
                                      <div className="flex items-center gap-1">
                                        {option.value ? option.icon : null}
                                        {option.label}
                                      </div>
                                    </Button>
                                  </PreFilledByAITooltip>
                                );
                              })}
                            </div>
                          );
                        }}
                      </FieldConnector>
                    )}
                    {itemMeta.helpers.shouldShowVATAmountNonStandardWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        className="mb-0"
                        description={t('expenses.info.saved_with_non_standard_rate', {
                          VATAmount: formatMoney(round((item.VATAmount || 0) * (expense.currencyRate || 1)), expense.currency),
                        })}
                      />
                    )}
                    {itemMeta.helpers.shouldShowVATRecoverabilityConfirmationWarning && (
                      <Alert
                        type="warning"
                        className="mb-0"
                        {...showChatBtn}
                        name="de user selects non zero vat rate after ocr marked it as non recoverable"
                        title={t('expenses.warning.vat_not_recoverable.confirmation.title.de')}
                        description={t('expenses.warning.vat_not_recoverable.confirmation.text.de')}
                      />
                    )}
                    {!itemMeta.fields.VATRegime.isHidden && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.VATRegime)}
                        disabled={isFieldDisabled}
                        label={t('expense.vat_regime')}
                        setBoundingBoxField={setBoundingBoxField}
                        hint={
                          !itemMeta.fields.localVATRate.isHidden &&
                          !shouldExpandLocalVATFields && (
                            <div className="flex items-center">
                              <Helper className="mr-1">
                                <Helper.Title>{t('expenses.helper.local_VAT_rate.title.temp')}</Helper.Title>
                                <Helper.Paragraph>{t('expenses.helper.local_VAT_rate.text1.temp')}</Helper.Paragraph>
                                <Helper.Paragraph>{convertMarkdown(t('expenses.helper.local_VAT_rate.text2'))}</Helper.Paragraph>
                                <Helper.Paragraph>{convertMarkdown(t(`expenses.helper.local_VAT_rate.text3.${customerCountry}`))}</Helper.Paragraph>
                              </Helper>
                              <p className="mr-2">
                                <ReactMarkdown
                                  components={{
                                    a: ({ children }) => (
                                      <Button structure="text" className="underline" onClick={() => setShouldExpandLocalVATFields(true)}>
                                        {children}
                                      </Button>
                                    ),
                                  }}
                                >
                                  {t('expense.local_vat.hint.' + customerCountry, { localVATRate: Math.round((item.localVATRate || 0) * 100) })}
                                </ReactMarkdown>
                              </p>
                              <Button structure="text" onClick={() => setShouldExpandLocalVATFields(true)}>
                                <EditOutlineIcon className="w-5 h-5" />
                              </Button>
                            </div>
                          )
                        }
                      >
                        {({ field }) => (
                          <VATRegime
                            {...field}
                            aiTooltip={{
                              enabled: guessedItem?.vat_regime.value != null && field.value === guessedItem.vat_regime.value,
                              description: translateGuessReason(guessedItem?.vat_regime.reason),
                            }}
                            tracing={{
                              onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDVATREGIME,
                              onChange: {
                                name: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDVATREGIME,
                                data: (value: string) => ({ value }),
                              },
                            }}
                            suggestedValue={guessedItem?.vat_regime.value}
                            masterVATRegime={formMeta.helpers.masterVATRegime}
                            hasManyItemsWithMasterVATRegime={formMeta.helpers.hasManyItemsWithMasterVATRegime}
                            allowedVATRegimes={itemMeta.helpers.allowedVATRegimes}
                            disallowedVATRegimes={itemMeta.helpers.disallowedVATRegimes}
                            hiddenVATRegimes={itemMeta.helpers.hiddenVATRegimes}
                            disabled={field.disabled}
                            onChange={(value: ExpenseVATRegime) => {
                              const itemIndex = index;
                              if (formMeta.helpers.mixableVATRegimes.includes(value)) {
                                return onChangeVATRegime(index, value, { shouldValidate: true });
                              }
                              return formValues.items.forEach((item, index) => {
                                if (index !== itemIndex) {
                                  if (item.VATRegime == null) return;
                                  if (formMeta.helpers.mixableVATRegimes.includes(item.VATRegime)) return;
                                }
                                onChangeVATRate(index, 0, { shouldValidate: true });
                                onChangeVATRegime(index, value, { shouldValidate: true });
                              });
                            }}
                          />
                        )}
                      </FieldConnector>
                    )}
                    {!itemMeta.fields.localVATRate.isHidden && shouldExpandLocalVATFields && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.localVATRate)}
                        disabled={isFieldDisabled}
                        highlightID={`localVATRate.${item.currencyAmount}`}
                        label={t('expenses.local_vat_rate_picker_question', {
                          userCountry: t(`country.${customerCountry}`),
                        })}
                        helper={
                          <Helper>
                            <Helper.Title>
                              {t('expenses.helper.local_VAT_rate.field.title', {
                                userCountry: t(`country.${customerCountry}`),
                              })}
                            </Helper.Title>
                            <Helper.Paragraph className="whitespace-pre-wrap">
                              {t('expenses.helper.local_VAT_rate.field.desc', {
                                userCountry: t(`country.${customerCountry}`),
                              })}
                            </Helper.Paragraph>
                          </Helper>
                        }
                      >
                        {({ field }) => {
                          return (
                            <div className="flex flex-wrap gap-2">
                              {VATRateOptions.map(({ label, value, icon }) => {
                                const isSelected = field.value === value;
                                return (
                                  <Button
                                    key={value}
                                    asDefault
                                    disabled={field.disabled}
                                    onClick={() => field.onChange(value)}
                                    tracingEvent={isSelected ? undefined : { name: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDLOCALVATRATE, data: { value } }}
                                    className={classNames('border rounded-full py-0.5 px-3 disabled:cursor-not-allowed disabled:bg-primary-50', {
                                      'bg-expenses-100 text-expenses': isSelected,
                                      'border-expenses': isSelected && field.disabled,
                                      'border-transparent': isSelected && !field.disabled,
                                    })}
                                  >
                                    <div className="flex items-center gap-1">
                                      {icon}
                                      {label}
                                    </div>
                                  </Button>
                                );
                              })}
                            </div>
                          );
                        }}
                      </FieldConnector>
                    )}
                    {itemMeta.helpers.shouldShowExtraGoodImportInfo && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="extra good import"
                        description={t('expenses.info_bubble.extra_reverse_good')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowreverseChargeZeroRateWarning && (
                      <Alert
                        type="warning"
                        {...showChatBtn}
                        name="reverse charge with zero local vat rate"
                        description={
                          <div className="flex gap-4">
                            <span>
                              {t('expenses.helper.reverse_charge_zero_rate.text', {
                                defaultVATRate: defaultVATRate * 100 + '%',
                              })}
                            </span>
                            <span className="flex-shrink-0">
                              <Helper>
                                <Helper.Paragraph>
                                  {t('expenses.helper.reverse_charge_zero_rate.helper', {
                                    defaultVATRate: defaultVATRate * 100 + '%',
                                  })}
                                </Helper.Paragraph>
                              </Helper>
                            </span>
                          </div>
                        }
                      />
                    )}
                    {itemMeta.helpers.shouldShowIntraReverseBESubject100ProWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="intra reverse be subject 100 pro warning"
                        description={t('expenses.info_bubble.intra_reverse_subject_100_pro.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowIntraReverseBEFranshiseeWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="intra reverse be franshisee warning"
                        description={t('expenses.info_bubble.intra_reverse_franshisee.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowIntraReverseBESubjectPartProWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="intra reverse be subject part pro warning"
                        description={t('expenses.info_bubble.intra_reverse_subject_part_pro.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowLocalReverseBEWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="local reverse be warning"
                        description={t('expenses.info_bubble.local_reverse.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowExrtaServiceBESubjectWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="extra service be subject warning"
                        description={t('expenses.info_bubble.extra_service_subject.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowExrtaServiceBEFranshiseeWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="extra service be franshisee warning"
                        description={t('expenses.info_bubble.extra_service_franshisee.be')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowIntraReverseDESubject100ProWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="intra reverse de subject 100 pro warning"
                        description={t('expenses.info_bubble.intra_reverse_subject_100_pro.de')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowIntraReverseDEFranshiseeWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="intra reverse de franshisee warning"
                        description={t('expenses.info_bubble.intra_reverse_franshisee.de')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowIntraReverseDESubjectPartProWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="intra reverse de subject part pro warning"
                        description={t('expenses.info_bubble.intra_reverse_subject_part_pro.de')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowLocalReverseDEWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="local reverse de warning"
                        description={t('expenses.info_bubble.local_reverse.de')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowExtraServiceDEWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        {...showChatBtn}
                        name="extra service de warning"
                        description={t('expenses.info_bubble.extra_service.de')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowOtherVATBEWarning && (
                      <Alert
                        type="warning"
                        className="mb-0"
                        {...showChatBtn}
                        name="other vat be warning"
                        description={
                          <div className="flex gap-2">
                            <p>{t('expenses.info_bubble.other_vat.be')}</p>
                            <Helper>
                              <Helper.Paragraph>{t('expenses.info_bubble.other_vat.helper.be')}</Helper.Paragraph>
                            </Helper>
                          </div>
                        }
                      />
                    )}
                    {itemMeta.helpers.shouldShowOtherVATDEWarning && (
                      <Alert
                        type="warning"
                        className="mb-0"
                        {...showChatBtn}
                        name="other vat de warning"
                        description={
                          <div className="flex gap-2">
                            <p>{t('expenses.info_bubble.other_vat.de')}</p>
                            <Helper>
                              <Helper.Paragraph>{t('expenses.info_bubble.other_vat.helper.de')}</Helper.Paragraph>
                            </Helper>
                          </div>
                        }
                      />
                    )}
                    {itemMeta.helpers.shouldShowVehiclesMonthlyLeasingAmountFieldWarning && (
                      <Alert
                        type="warning"
                        {...showChatBtn}
                        className="mb-0"
                        name="monthly leasing with more than 2000 euro"
                        description={t('expenses.warning_bubble.monthly_leasing_high_amount')}
                      />
                    )}
                    <FieldConnector
                      showUI={!itemMeta.fields.professionalPart.isHidden}
                      name={getName(ExpenseFormItemNames.professionalPart)}
                      disabled={(name) => isFieldDisabled(name) || itemMeta.fields.professionalPart.isDisabled}
                      label={t('expense.professionalPart')}
                      helper={
                        <Helper>
                          <Helper.Title>{t('helper_professional_part_title')}</Helper.Title>
                          <Helper.Paragraph>
                            {item.category && autoMobileBE.includes(item.category.id)
                              ? t('helper_professional_part_text1.cars')
                              : t('helper_professional_part_text1')}
                          </Helper.Paragraph>
                          <Helper.Title>{t('helper_professional_part_subtitle')}</Helper.Title>
                          <Helper.Paragraph>{t('helper_professional_part_text2.' + customerCountry)}</Helper.Paragraph>
                        </Helper>
                      }
                    >
                      {({ field: { value, onChange, error, name, disabled } }) => {
                        return (
                          <NumberField
                            immedialtyCommitOnChange
                            highlightID={name}
                            disabled={disabled}
                            error={error}
                            aiTooltip={{
                              enabled: value != null && value === guessedItem?.professional_part.value,
                              description: translateGuessReason(guessedItem?.professional_part.reason),
                            }}
                            tracing={{
                              onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDPROFESSIONNALUSE,
                            }}
                            renderExtraRight={(className) =>
                              value != null && (
                                <span className={classNames('text-primary-400 flex items-center', className)}>
                                  {formatMoney((item.currencyAmount || 0) * value, formValues.currency)}
                                </span>
                              )
                            }
                            format={{ prefix: '%' }}
                            value={value == null ? null : round(value * 100)}
                            onChange={(value) => {
                              onChange(value == null ? null : +(value / 100).toFixed(4));
                            }}
                          />
                        );
                      }}
                    </FieldConnector>

                    {itemMeta.helpers.shouldShowBusinessCarPurchaseWarning && (
                      <Alert
                        type="info"
                        className="mb-0"
                        contentClassName="whitespace-break-spaces"
                        title={t('expenses.info_bubble.business_car_purchase.professional_part.title')}
                        description={t('expenses.info_bubble.business_car_purchase.professional_part.text')}
                      />
                    )}

                    {itemMeta.helpers.shouldShowProfPartRentSecWorkplaceWarning && (
                      <Alert
                        type="info"
                        description={t('expenses.info_bubble.professional_part_rent_secondary')}
                        renderButtons={(className) => (
                          <a
                            className={classNames('text-sm font-semibold underline', className)}
                            href={t('expenses.info_bubble.professional_part_rent_secondary.link')}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('learn_more')}
                          </a>
                        )}
                      />
                    )}
                    {itemMeta.helpers.shouldShowCompanyProfessionalPartWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        dismissable
                        dismissId={AlertIdsEnum.companyProfessionalPartWarning}
                        description={t('expenses.info_bubble.company_professional_part')}
                      />
                    )}
                    {itemMeta.helpers.shouldShowProfessionalDeductibilityWarning && (
                      <Alert
                        type="warning"
                        {...showChatBtn}
                        name="deductibility same as professional part"
                        description={t('expenses.helper.deductibility_equals_professional_part.text', {
                          incomeTaxDeductibility: Math.round((item.incomeTaxDeductibility || 0) * 100),
                          professionalPart: Math.round((item.professionalPart || 0) * 100),
                          incomeTaxDeductibility_by_professionalPart: Math.round((item.incomeTaxDeductibility || 0) * (item.professionalPart || 0) * 100),
                        })}
                      />
                    )}
                    {itemMeta.helpers.shouldShowProfessionalPartDifferenceWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        name="professional part is not the recommended by the category"
                        description={t('expenses.helper.professional_part_not_equal_to_recommened.text', {
                          recommendedPercentage: Math.round((item.category?.recommendedProfessionalPart || 0) * 100),
                          categoryDisplayName: item.category && categories.dict[item.category.id]?.displayName,
                          enteredPercentage: Math.round((item.professionalPart || 0) * 100),
                        })}
                      />
                    )}
                    {itemMeta.helpers.shouldShowWorkFromHomeProfessionalPartWarning && (
                      <Alert
                        type="info"
                        renderButtons={(className) => (
                          <a
                            className={classNames('text-sm font-semibold underline', className)}
                            href={t('expenses.warning_bubble.professional_part_work_from_home_categories.learn_more.' + customerCountry)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('learn_more')}
                          </a>
                        )}
                        name="professional part warning for work from home categories"
                        description={t('expenses.warning_bubble.professional_part_work_from_home_categories.' + customerCountry)}
                      />
                    )}
                    {itemMeta.helpers.shouldShowBankChargesProfessionalPartWarning && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        name="category bank charges and professional part is not 100"
                        description={t('expenses.helper.professional_part_bank_charges_not_100')}
                      />
                    )}
                    {!itemMeta.fields.incomeTaxDeductibility.isHidden && (
                      <FieldConnector
                        name={getName(ExpenseFormItemNames.incomeTaxDeductibility)}
                        disabled={isFieldDisabled}
                        label={t(`expense.item.${formMeta.helpers.isCustomerOnePersonCompany ? 'corporate' : 'income'}_tax_deductibility`)}
                      >
                        {({ field: { value, onChange, name, disabled } }) => (
                          <NumberField
                            highlightID={name}
                            disabled={disabled}
                            format={{ prefix: '%' }}
                            value={value == null ? null : Math.round(value * 100)}
                            onChange={(value) => onChange(value == null ? 0 : value / 100)}
                          />
                        )}
                      </FieldConnector>
                    )}
                    {!itemMeta.fields.maxDeductibleVAT.isHidden && (
                      <>
                        <FieldConnector
                          name={getName(ExpenseFormItemNames.maxDeductibleVAT)}
                          disabled={isFieldDisabled}
                          label={t('expense.item.max_deductible_vat')}
                        >
                          {({ field: { value, onChange, name, disabled } }) => (
                            <NumberField
                              highlightID={name}
                              disabled={disabled}
                              format={{ prefix: '%' }}
                              value={value == null ? null : Math.round(value * 100)}
                              onChange={(value) => onChange(value == null ? 0 : value / 100)}
                            />
                          )}
                        </FieldConnector>
                        {itemMeta.helpers.shouldShowMaxDeductibleVATVehicleInfoBox && (
                          <Alert
                            type="info"
                            {...showChatBtn}
                            name="max deductible vat info box"
                            description={t('expenses.info_bubble.expert.max_deductible_vat_vehicle.be')}
                          />
                        )}
                      </>
                    )}
                    {!itemMeta.fields.amortizationPeriod.isHidden && (
                      <>
                        <FieldConnector
                          name={getName(ExpenseFormItemNames.amortizationPeriod)}
                          disabled={isFieldDisabled}
                          highlightID={`amortizationPeriod.${item?.currencyAmount}`}
                          onShow={(name) => {
                            const duration = getRecommendedAmortizationDurationInYears(item.category);
                            if (duration && item.amortizationPeriod == null) {
                              setValue(name, duration * 12);
                            }
                          }}
                          label={t('expense.field.amortization.amortization_period')}
                          helper={
                            <Helper>
                              <Helper.Title>
                                <ReactMarkdown components={markdownComponents}>{t('expense.amortization_period.helper.title1')}</ReactMarkdown>
                              </Helper.Title>
                              <Helper.Paragraph>
                                <ReactMarkdown components={markdownComponents}>{t('expense.amortization_period.helper.text1')}</ReactMarkdown>
                              </Helper.Paragraph>
                              <Helper.Paragraph>
                                <ReactMarkdown components={markdownComponents}>
                                  {t(`expense.amortization_period.helper.text2.${customerCountry}`)}
                                </ReactMarkdown>
                              </Helper.Paragraph>
                              <Helper.Title>
                                <ReactMarkdown components={markdownComponents}>{t('expense.amortization_period.helper.title2')}</ReactMarkdown>
                              </Helper.Title>
                              <Helper.Paragraph>
                                <ReactMarkdown components={markdownComponents}>{t('expense.amortization_period.helper.text3')}</ReactMarkdown>
                              </Helper.Paragraph>
                            </Helper>
                          }
                        >
                          {({ field }) => <AmortizationPeriod {...field} category={item.category} />}
                        </FieldConnector>
                        {itemMeta.helpers.shouldShowAmortizationStartDateWarning && (
                          <Alert
                            type="warning"
                            className="mb-0"
                            description={
                              <div className="flex gap-4">
                                <span>{t('expenses.warning_bubble.purchase_before_start_date.title')}</span>
                                <Helper>
                                  <Helper.Paragraph>
                                    {t('expenses.warning_bubble.purchase_before_start_date.text', { startDate: dayjs(customer?.startDate).format(dateFormat) })}
                                  </Helper.Paragraph>
                                  <Helper.Paragraph>{t('expenses.warning_bubble.purchase_before_start_date.text2')}</Helper.Paragraph>
                                </Helper>
                              </div>
                            }
                          />
                        )}
                      </>
                    )}

                    {itemMeta.helpers.shouldShowRenovationAmortInfo && (
                      <Alert
                        type="info"
                        {...showChatBtn}
                        className="mb-0"
                        name="renovation amortization"
                        description={t('expenses.info_bubble.renovation.amortization')}
                      />
                    )}

                    <FieldConnector
                      showUI={!itemMeta.fields.name.isHidden}
                      name={getName(ExpenseFormItemNames.name)}
                      disabled={isFieldDisabled}
                      label={t('expense.field.name.label')}
                    >
                      {({ field: { onChange, ...field } }) => (
                        <FreeTextInput
                          tracing={{
                            onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDAMORTISATIONITEMNAME,
                          }}
                          {...field}
                          onChange={({ target: { value } }) => {
                            onChange(value);
                          }}
                        />
                      )}
                    </FieldConnector>
                  </div>
                </>
              );
            })}
            {formMeta.helpers.shouldShowNewItemBtn && !isLocked && (
              <div>
                <Button
                  structure="text"
                  onClick={() => {
                    itemsController.append({
                      ...itemDefault,
                    });
                  }}
                >
                  <RowsOutlineIcon className="w-5 mr-1" />
                  {formMeta.helpers.isImportVATForm ? t('invoice.field.item.add_import_fees_item') : t('invoice.field.item.add_item')}
                </Button>
              </div>
            )}

            {formMeta.helpers.shouldShowCalcRows && (
              <div className="mt-7">
                <div className="bg-[#F9F7FC] flex items-center justify-between rounded-lg p-2">
                  <p className="text-primary-400">{t('expense.price_total_excluding_VAT')}</p>
                  <span className="font-semibold">
                    <Amount amount={AllItemsAmountExclVAT} currency={formValues.currency} />
                  </span>
                </div>

                <div className="bg-[#F9F7FC] flex items-center justify-between rounded-lg p-2 my-1">
                  <p className="text-primary-400">{t('expense.price_total_VAT')}</p>
                  <span className="font-semibold">
                    <Amount amount={AllItemsVATAmounts} currency={formValues.currency} />
                  </span>
                </div>

                {formMeta.helpers.isImportVATForm && (
                  <div className="bg-[#F9F7FC] flex items-center justify-between rounded-lg p-2 my-1">
                    <p className="text-primary-400">{t('expense.price_import_VAT')}</p>
                    <span className="font-semibold">
                      <Amount amount={formValues.items[0].currencyAmount || 0} currency={formValues.currency} />
                    </span>
                  </div>
                )}

                <div className="bg-primary-400 flex items-center justify-between rounded-lg p-2">
                  <p className="text-white">{t('expense.price_total_including_VAT')}</p>
                  <span className="font-semibold">
                    <Amount className="!text-white" amount={AllItemsAmountInclVAT} currency={formValues.currency} />
                  </span>
                </div>
              </div>
            )}

            {isWeb && basketExpense && basketExpense?.amount?.value !== AllItemsAmountInclVAT && !isBasketAmountAlertDismissed ? (
              <Alert
                description={t('expense.basket.amount_change_alert.content', {
                  amount: formatMoney(basketExpense?.amount?.value || 0),
                  newAmount: formatMoney(AllItemsAmountInclVAT),
                })}
                type="warning"
                renderButtons={(className) => (
                  <div className="flex items-center gap-3 mt-2">
                    <Button
                      asDefault
                      className={classNames(className, 'text-sm font-semibold underline')}
                      onClick={() => {
                        dismissBasketAmountAlert(true);
                      }}
                    >
                      {t('expense.basket.amount_change_alert.keep_cta', {
                        amount: formatMoney(basketExpense?.amount?.value || 0),
                      })}
                    </Button>

                    <Button
                      asDefault
                      className={classNames(className, 'text-sm font-semibold underline')}
                      loading={updateExpenseInBasketAPI.isLoading}
                      onClick={() =>
                        updateExpenseInBasketAPI.mutate({
                          ...basketExpense,
                          amount: {
                            value: AllItemsAmountInclVAT,
                            currency: CurrenciesEnum.EUR,
                          },
                        })
                      }
                    >
                      {t('expense.basket.amount_change_alert.update_cta', {
                        amount: formatMoney(AllItemsAmountInclVAT),
                      })}
                    </Button>
                  </div>
                )}
              />
            ) : null}

            <div className="flex flex-col items-end mt-10">
              <TaxImpact documentType={TaxImpactDocumentType.EXPENSE} />
            </div>

            <div className="mt-7 flex flex-col gap-6">
              {(formMeta.fields.paymentStatus.isApplicable || formMeta.fields.currencyRate.isApplicable) && (
                <p className="-mx-6 px-6 py-2 text-primary-600 bg-primary-50 font-bold text-xl leading-tight font-degular">
                  {t('expense.payment_information')}
                </p>
              )}

              {!formMeta.fields.currencyRate.isHidden && (
                <FieldConnector
                  name={ExpenseFormNames.currencyRate}
                  disabled={isFieldDisabled}
                  label={`${t('expense.field.paymentAmount')} ${
                    AllItemsAmountInclVAT ? `(${t('invoice.form.exchange_rate')} ${truncateNumber(formValues.currencyRate || 0, 3)})` : ''
                  }`}
                >
                  {({ field: { value, onChange, ...field } }) => {
                    return (
                      <>
                        <NumberField
                          tracing={{
                            onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDCURRENCYRATE,
                          }}
                          {...field}
                          format={{ asMoney: true }}
                          loading={currencyRatesQuery.isFetching}
                          value={!value ? 0 : (AllItemsAmountInclVAT / value) * (formValues.isCreditNote ? -1 : 1)}
                          onChange={(amountInEuro) => {
                            if (!amountInEuro) {
                              return onChange(null);
                            }
                            const roundAmount = Math.abs(round(amountInEuro));
                            const isCreditNote = amountInEuro < 0;
                            if (allowDynamicTypeSwitching) {
                              setValue(ExpenseFormNames.isCreditNote, isCreditNote);
                            }
                            if (!AllItemsAmountInclVAT) {
                              if (!value) {
                                onChangeAmount(0, roundAmount);
                                return onChange(1);
                              }
                              onChangeAmount(0, round(roundAmount * value));
                              return onChange(value);
                            }
                            onChange(AllItemsAmountInclVAT / Math.abs(amountInEuro));
                          }}
                        />
                        {currencyRatesQuery.isError && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-primary-400">{t('backend.error.something_went_wrong')}.</span>
                            <Button
                              structure="text"
                              color="primary"
                              onClick={() =>
                                currencyRatesQuery.refetch({
                                  cancelRefetch: true,
                                })
                              }
                            >
                              <span className="text-sm">{t('retry')}</span>
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  }}
                </FieldConnector>
              )}

              {!formMeta.fields.paymentStatus.isHidden && (
                <FakeField>
                  {() => {
                    const name = ExpenseFormNames.paymentStatus;
                    const value = formAPI.getValues(name);
                    return (
                      <SegmentedToggle
                        value={value}
                        disabled={isFieldDisabled(name) || formMeta.fields.paymentStatus.isDisabled}
                        options={[
                          formMeta.helpers.isPending
                            ? {
                                label: (
                                  <div className="flex items-center justify-center gap-1">
                                    <ClockIconOutline className="w-5 h-5" />
                                    <span>{t('expense.payment_status.pending')}</span>
                                  </div>
                                ),
                                value: ExpensePaymentStatusType.pending,
                              }
                            : { label: t('expense.payment_status.paid'), value: ExpensePaymentStatusType.paid },
                          { label: t('expense.payment_status.not_paid'), value: ExpensePaymentStatusType.unpaid },
                        ]}
                        onChange={(status) => {
                          formAPI.setValue(name, status, { shouldDirty: true });
                          logAmplitude(ExpensesEvents.WEBAPP_EXPENSE_PAYMENTSTATUS_CHANGED, {
                            from: value,
                            to: status,
                          });
                        }}
                      />
                    );
                  }}
                </FakeField>
              )}

              {!formMeta.fields.transactions.isHidden && (
                <FieldConnector
                  name={ExpenseFormNames.transactions}
                  disabled={isFieldDisabled}
                  label={`${t('expense.link_payment.without_transactions')} (${t('field.optional.tag')})`}
                >
                  {({ field: { onChange, value, ...field } }) => {
                    const fieldOnChange = (selected: any) => {
                      const options = selected as any[];
                      const latestOption = last(options);
                      const isNonSelected = isEmpty(options);
                      const isPreset = !!latestOption?.preset;

                      if (isPreset) {
                        presetsOptions.forEach((option) => {
                          if (option.value === latestOption.value)
                            return setValue(option.value, true, {
                              shouldDirty: true,
                            });
                          setValue(option.value, false, { shouldDirty: true });
                        });

                        updatePaymentDateBasedOnDate(formValues.expenseDate, () => {
                          updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
                        });

                        return onChange([]);
                      }

                      presetsOptions.forEach((option) => setValue(option.value, false, { shouldDirty: true }));

                      if (isNonSelected) return onChange([]);

                      onChange(options.map(({ transaction }) => transaction));

                      const latestTransactionDate = getLatestTransactionsDate(formAPI.getValues(field.name));
                      if (!latestTransactionDate) return;

                      updatePaymentDateBasedOnDate(latestTransactionDate, () => {
                        updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
                      });
                    };

                    return (
                      <PaymentLinkSelect
                        tracing={{
                          onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDLINKPAYMENT,
                          onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDLINKPAYMENT,
                        }}
                        optionsDict={getOptionsDict(formValues?.payments?.transactions?.map(transactionToOption))}
                        inputValue={hasTransactions ? supplierNameToSearchValue(formValues.supplier?.name) : undefined}
                        {...field}
                        value={((value || []) as Transaction[]).map((t) => t._id)}
                        cacheKey={expense._id || 'expense_payments_list'}
                        isDisabled={field.disabled}
                        onChange={fieldOnChange}
                        fixedOptions={presetsOptions}
                        renderLabelContent={(selected) => {
                          if (!hasTransactions) return null;
                          const selectedPreset = presetsOptions.find((option) => get(formValues, option.value));
                          if ((!selected || !selected.length) && !selectedPreset) return null;

                          return (
                            <div className="mt-3">
                              {selectedPreset?.value === ExpenseFormNames.other_accounts || selectedPreset?.value === ExpenseFormNames.cash ? (
                                <LinkedItemsProgress type="expenses" total={100} matchedAmount={100} />
                              ) : (
                                <LinkedItemsProgress
                                  type="expenses"
                                  total={
                                    round(AllItemsAmountInclVAT / (formValues.currencyRate ?? 1)) +
                                    getExpenseLinkedPaymentsOppositeDirectionTotal(selected, formValues.isCreditNote)
                                  }
                                  matchedAmount={getExpenseLinkedPaymentsAmount(selected, formValues.isCreditNote)}
                                />
                              )}
                            </div>
                          );
                        }}
                        renderSelected={(selected) => {
                          const selectedPreset = presetsOptions.find((option) => get(formValues, option.value));
                          if (!selected?.length && !selectedPreset) return null;

                          return (
                            <div className="flex flex-col mt-2">
                              {selectedPreset && (
                                <TransactionOption2
                                  className="p-4 bg-primary-25 border border-primary-100 rounded-lg"
                                  isDisabled={field.disabled}
                                  transaction={{ counterPartyName: selectedPreset.label }}
                                  onDelete={() => {
                                    setValue(selectedPreset.value, false, {
                                      shouldDirty: true,
                                    });
                                  }}
                                />
                              )}

                              {selected?.map((option: TransactionAsOption) => {
                                return (
                                  <TransactionOption2
                                    key={option.value}
                                    transaction={option.transaction}
                                    isDisabled={field.disabled}
                                    className="p-4 bg-primary-25 border-b border-l border-r border-primary-100 first:rounded-t-lg first:border-t last:rounded-b-lg"
                                    onDelete={() => fieldOnChange(selected.filter((selectedOption: any) => selectedOption.value !== option.value))}
                                  />
                                );
                              })}
                            </div>
                          );
                        }}
                      />
                    );
                  }}
                </FieldConnector>
              )}

              {!formMeta.fields.isSuggestedTransactionActive.isHidden && (
                <FieldConnector name={ExpenseFormNames.isSuggestedTransactionActive} className={formValues.isSuggestedTransactionActive ? '' : 'hidden'}>
                  {({ name }) => {
                    return (
                      <SuggestedTransaction
                        data={{
                          amount: AllItemsAmountInclVAT * (formValues.isCreditNote ? 1 : -1),
                          currency: formValues.currency,
                          date: formValues.payments?.status?.payment_date || formValues.expenseDate,
                          communication: expense.guessed_data?.structured_communication,
                          name: formValues.supplier?.name,
                          IBAN: expense.guessed_data?.supplier?.iban,
                        }}
                        tracing={{
                          onAccept: ExpensesEvents.WEBAPP_EXPENSE_SUGGESTEDTRANSACTION_CONFIRMED,
                          onDecline: ExpensesEvents.WEBAPP_EXPENSE_SUGGESTEDTRANSACTION_DECLINED,
                        }}
                        onSuggestionChange={(transaction) => formAPI.setValue(name, !!transaction)}
                        onDecline={() => formAPI.setValue(name, false)}
                        onAccept={(transaction) => {
                          formAPI.setValue(name, false);
                          const paymentStatus = formAPI.getValues(ExpenseFormNames.paymentStatus);
                          formAPI.setValue(ExpenseFormNames.paymentStatus, ExpensePaymentStatusType.paid);
                          formAPI.setValue(ExpenseFormNames.transactions, [transaction]);
                          if (!transaction.valueDate || paymentStatus === ExpensePaymentStatusType.unpaid) return;
                          updatePaymentDateBasedOnDate(transaction.valueDate, () => {
                            updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
                          });
                        }}
                      />
                    );
                  }}
                </FieldConnector>
              )}

              {!formMeta.fields.paymentDate.isHidden && (
                <>
                  <FieldConnector
                    name={ExpenseFormNames.paymentDate}
                    disabled={isFieldDisabled}
                    label={t('expense.paymentDate.label')}
                    helper={
                      <Helper>
                        <Helper.Title>{t('expense.paymentDate.helper.title')}</Helper.Title>
                        <Helper.Paragraph>{t('expense.paymentDate.helper.desc')}</Helper.Paragraph>
                      </Helper>
                    }
                    onShow={(name) => {
                      if (formAPI.getValues(name) != null) return;
                      const expenseDate = formAPI.getValues(ExpenseFormNames.expenseDate);
                      const transactions = formAPI.getValues(ExpenseFormNames.transactions);
                      const latestTransactionDate = getLatestTransactionsDate(transactions);
                      const guessedDueDate = guessedData?.payment_date.value;
                      updatePaymentDateBasedOnDate(latestTransactionDate || guessedDueDate || expenseDate);
                    }}
                  >
                    {({ field: { value, onChange, ...field } }) => {
                      return (
                        <DatePicker
                          {...field}
                          selected={value}
                          tracing={{
                            onChange: ExpensesEvents.WEBAPP_EXPENSE_PAYMENTDATE_SET,
                          }}
                          onChange={(date) => {
                            const newValue = date ? dayjs(date).format('YYYY-MM-DD') : null;
                            if (!newValue) return;
                            onChange(newValue);
                            updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
                          }}
                        />
                      );
                    }}
                  </FieldConnector>

                  {formMeta.helpers.isCustomerDE && !!lockBytaxesStatusAPI.data?.date && (
                    <ExpenseSubmitLockedByTaxes
                      isCreditNote={!!formValues.isCreditNote}
                      fieldSlug={'payment_date'}
                      taxLockReasons={lockBytaxesStatusAPI.data.date}
                    />
                  )}

                  {formMeta.helpers.shouldShowPaymentDateWarning && (
                    <Alert
                      type="info"
                      name="payment date in the future"
                      className="!my-0"
                      {...showChatBtn}
                      description={t('warning_bubble.document_date_in_the_future')}
                    />
                  )}
                </>
              )}

              {!formMeta.fields.dueDate.isHidden && (
                <FieldConnector
                  name={ExpenseFormNames.dueDate}
                  disabled={isFieldDisabled}
                  label={`${t('expense.dueDate.label')} (${t('field.optional.tag')})`}
                  helper={
                    <Helper>
                      <Helper.Paragraph>{t('expense.dueDate.helper')}</Helper.Paragraph>
                    </Helper>
                  }
                  onShow={(name) => {
                    if (formAPI.getValues(name) != null) return;
                    const guessedDueDate = guessedData?.due_date.value;
                    if (!guessedDueDate) return;
                    formAPI.setValue(name, guessedDueDate);
                  }}
                >
                  {({ field: { value, onChange, ...field } }) => {
                    return (
                      <DatePicker
                        {...field}
                        placeholder={t('expense.dueDate.placeHolder')}
                        selected={value}
                        tracing={{
                          onChange: ExpensesEvents.WEBAPP_EXPENSE_DUEDATE_ADDED,
                        }}
                        onChange={(date) => {
                          const newValue = date ? dayjs(date).format('YYYY-MM-DD') : null;
                          onChange(newValue);
                        }}
                      />
                    );
                  }}
                </FieldConnector>
              )}

              {formMeta.helpers.shouldShowPendingStatusWarning && (
                <Alert
                  type="warning"
                  className="mb-0"
                  renderIcon={(className) => <ClockIconOutline className={className} />}
                  title={t('expense.transaction.pending.warning.title')}
                  description={t('expense.transaction.pending.warning.desc')}
                />
              )}

              {!!canPayWithQRCode && (
                <div>
                  <PayWithQRCode
                    onMarkAsPaid={() => {
                      formAPI.setValue(ExpenseFormNames.paymentStatus, ExpensePaymentStatusType.paid);
                      const today = dayjs().format('YYYY-MM-DD');
                      updatePaymentDateBasedOnDate(today, () => {
                        updatePeriodBasedOnDate(ExpenseFormNames.paymentDate);
                      });
                    }}
                    data={{
                      supplier: formValues.supplier?.name || '',
                      iban: invoiceIBAN || '',
                      amount: AllItemsAmountInclVAT,
                      communication: expense.guessed_data?.structured_communication || '',
                    }}
                  />
                </div>
              )}

              {isWeb && basketExpense && formMeta.helpers.isPaid && !isBasketLinkedPaymentsAlertDimissed && (
                <Alert
                  description={t('expense.basket.linked_payment_change_alert.content', {
                    amount: formatMoney(basketExpense?.amount?.value || 0),
                  })}
                  type="warning"
                  renderButtons={(className) => (
                    <div className="flex items-center gap-3 mt-2">
                      <Button
                        asDefault
                        className={classNames(className, 'text-sm font-semibold underline')}
                        onClick={() => {
                          dismissBasketLinkedPaymentsAlert(true);
                        }}
                      >
                        {t('expense.basket.linked_payment_change_alert.keep_cta')}
                      </Button>
                      <Button
                        asDefault
                        className={classNames(className, 'text-sm font-semibold underline')}
                        loading={deleteItemAPI.isLoading}
                        onClick={() => deleteItemAPI.mutate(basketExpense._id)}
                      >
                        {t('hide')}
                      </Button>
                    </div>
                  )}
                />
              )}

              {!basketQuery.isLoading &&
                canAccessAccountableBanking &&
                formMeta.helpers.isUnpaid &&
                !formMeta.helpers.isCreditNote &&
                formMeta.helpers.isEuroExpense && (
                  <AddExpenseToBasket expense={formValues} validateExpense={formAPI.trigger} isAddedToBasket={!!basketExpense} />
                )}

              <p className="-mx-6 px-6 py-2 text-primary-600 bg-primary-50 font-bold text-xl leading-tight font-degular">
                {t('expense.additional_information')}
              </p>

              {!formMeta.fields.period.isHidden && (
                <FieldConnector
                  name={ExpenseFormNames.period}
                  disabled={isFieldDisabled}
                  onShow={(name) => {
                    if (formAPI.getValues(name) != null) return;
                    updatePeriodBasedOnDate([ExpenseFormNames.paymentDate, ExpenseFormNames.expenseDate]);
                  }}
                  errorKeys={['period', 'period.year', 'period.month', 'period.quarter']}
                  label={t('fields.vat_declaration.label')}
                  helper={<PeriodHelper />}
                  loading={lockBytaxesStatusAPI.isLoading}
                >
                  {({ field }) => (
                    <Select
                      tracing={{
                        onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDPERIOD,
                        onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDPERIOD,
                      }}
                      menuPlacement="top"
                      options={periodOptions}
                      placeholder={t('expense.period_field.placeholder')}
                      {...field}
                      isDisabled={field.disabled}
                      value={field.value ? periodToValue(field.value, documentVATReturnFrequency) : null}
                      onChange={(option: any) => field.onChange(option.period)}
                    />
                  )}
                </FieldConnector>
              )}
              {formMeta.helpers.shouldShow10DaysRuleWarning && (
                <Alert type="warning" className="mb-0" description={t('expense.10_days_rule.period_date_mismatch.warning.de')} />
              )}
              {!!lockBytaxesStatusAPI.data?.period && (
                <ExpenseSubmitLockedByTaxes isCreditNote={!!formValues.isCreditNote} fieldSlug={'period'} taxLockReasons={lockBytaxesStatusAPI.data.period} />
              )}

              <FieldConnector
                name={ExpenseFormNames.notes}
                label={`${t('expense.notes')} (${t('field.optional.tag')})`}
                disabled={isFieldDisabled}
                helper={
                  <Helper>
                    <Helper.Paragraph>{t('expense.user_comments.helper')}</Helper.Paragraph>
                  </Helper>
                }
              >
                {({ field: { onChange, ...field } }) => (
                  <FreeTextInput
                    tracing={{
                      onFocus: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDNOTES,
                    }}
                    type="textarea"
                    rows={1}
                    placeholder={t('expense.notes_field.placeholder')}
                    {...field}
                    onChange={({ target: { value } }) => {
                      onChange(value || null);
                    }}
                  />
                )}
              </FieldConnector>

              <Button structure="text" className="self-start" onClick={() => setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)}>
                {isAdvancedSettingsOpen ? <MinusIcon className="w-5 mr-1" /> : <PlusIcon className="w-5 mr-1" />}
                {t('invoice.item_form.advanced_settings')}
              </Button>
              {isAdvancedSettingsOpen && (
                <>
                  <FieldConnector
                    name={ExpenseFormNames.userVATType}
                    disabled={isFieldDisabled}
                    label={t('customer.field.vattype.doctype', {
                      docType: t('expense'),
                    })}
                  >
                    {({ field: { onChange, ...rest } }) => (
                      <Select
                        tracing={{
                          onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDVATTYPE,
                          onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDVATTYPE,
                        }}
                        menuPlacement="top"
                        options={[
                          {
                            label: t('settings.generalTaxes.subjectToVAT'),
                            value: VATTypeEnum.subjectToVAT,
                          },
                          {
                            label: t(`settings.generalTaxes.franchisee.${customerCountry}`),
                            value: VATTypeEnum.franchisee,
                          },
                          {
                            label: t('settings.generalTaxes.exempt'),
                            value: VATTypeEnum.exempt,
                          },
                        ]}
                        isDisabled={rest.disabled}
                        isSearchable={false}
                        onChange={(option: any) => {
                          onChange(option.value);
                        }}
                        {...rest}
                      />
                    )}
                  </FieldConnector>
                  {!formMeta.fields.currency.isHidden && (
                    <FieldConnector
                      name={ExpenseFormNames.currency}
                      label={t('invoice.field.currency')}
                      disabled={isFieldDisabled}
                      setBoundingBoxField={setBoundingBoxField}
                    >
                      {({ field: { onChange, ...rest } }) => (
                        <Select
                          aiTooltip={{
                            enabled: guessedData?.currency.value != null && rest.value === guessedData.currency.value,
                            description: translateGuessReason(guessedData?.currency.reason),
                          }}
                          tracing={{
                            onMenuOpen: ExpensesEvents.WEBAPP_EXPENSE_CLICKEDSETTINGCURRENCY,
                            onChange: ExpensesEvents.WEBAPP_EXPENSE_UPDATEDSETTINGCURRENCY,
                          }}
                          isSearchable
                          placeholder={t('search') + '...'}
                          options={currenciesList.map((option) => ({
                            ...option,
                            label: t(option.slug),
                          }))}
                          menuPlacement="top"
                          formatOptionLabel={(option) => {
                            const iconPath = getCurrencyIconPath(option.value);
                            return (
                              <div className="flex items-center gap-2">
                                {!!iconPath && <img src={iconPath} className="w-6 h-6" />}
                                {option.label}
                              </div>
                            );
                          }}
                          onChange={(option: any) => {
                            if (option.value === rest.value) return;
                            onChange(option.value);
                            setShowSimpleCurrencySwitch(true);
                          }}
                          {...rest}
                          isDisabled={rest.disabled}
                        />
                      )}
                    </FieldConnector>
                  )}
                </>
              )}
              {(() => {
                const ublPath = expense.references?.ubl31_file_path;
                if (!ublPath) return null;
                const filePath = formValues.file?.path;
                const downloadNamePrefix = [formValues.supplier?.name, formValues.expenseDate];
                return (
                  <UseState
                    init={false}
                    render={(isOpen, setIsOpen) => {
                      return (
                        <>
                          <Button structure="text" className="self-start" onClick={() => setIsOpen(!isOpen)}>
                            {isOpen ? <MinusIcon className="w-5 mr-1" /> : <PlusIcon className="w-5 mr-1" />}
                            {t('form.attachments')}
                          </Button>
                          {isOpen && (
                            <FilesDownloadList
                              addFileTypeToFileLabels
                              files={[
                                {
                                  path: filePath,
                                  label: t('form.attachments.expense.main'),
                                  downloadNamePrefix,
                                },
                                {
                                  path: ublPath,
                                  label: t('form.attachments.ubl'),
                                  downloadNamePrefix,
                                },
                              ]}
                            />
                          )}
                        </>
                      );
                    }}
                  />
                );
              })()}
            </div>

            {basketExpense && formValues.currency !== CurrenciesEnum.EUR && !isBasketCurrencyAlertDismissed ? (
              <Alert
                title={t('expense.basket.currency_change_alert.content', {
                  amount: formatMoney(basketExpense?.amount?.value || 0),
                })}
                type="warning"
                renderButtons={(className) => (
                  <div className="flex items-center space-x-4 mt-3">
                    <Button asDefault className={className} size="xs" loading={deleteItemAPI.isLoading} onClick={() => deleteItemAPI.mutate(basketExpense._id)}>
                      {t('hide')}
                    </Button>

                    <Button
                      asDefault
                      className={className}
                      size="xs"
                      onClick={() => {
                        dismissBasketCurrencyAlert(true);
                      }}
                    >
                      {t('expense.basket.currency_change_alert.keep_cta')}
                    </Button>
                  </div>
                )}
              />
            ) : null}

            {isExpert && (
              <div className="border-t border-primary-100 mt-5 pt-7 flex flex-col gap-6">
                <p className="font-bold text-main-900 text-xl leading-none tracking-tight font-avenir">{t('expense.accountant_section')}</p>
                <FieldConnector
                  name={'accountantReview.comments'}
                  disabled={isFieldDisabled}
                  label={t('accountantReview.comments')}
                  helper={
                    <Helper>
                      <Helper.Paragraph>{t('expense.accountant_comments.helper')}</Helper.Paragraph>
                    </Helper>
                  }
                >
                  {({ field: { onChange, ...field } }) => (
                    <FreeTextInput
                      type="textarea"
                      {...field}
                      onChange={({ target: { value } }) => {
                        onChange(value || null);
                      }}
                    />
                  )}
                </FieldConnector>

                <ExpenseVATCells expense={formValues} enabled={!hasErrors} />
              </div>
            )}

            {footerMeta}
          </div>
          {!disabled &&
            (!isLocked || (isLocked && hasDirtyFields)) &&
            (() => {
              const runValidationAndSubmit: MouseEventHandler<HTMLButtonElement> = (e) => {
                if (lockBytaxesStatusAPI.data) {
                  const lockUI = document.querySelector('.TAX_LOCK_REASON');
                  if (lockUI) {
                    lockUI.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                    return;
                  }
                }
                const VATConflictUI = document.querySelector('.VAT_CONFLICT_ERROR');
                if (VATConflictUI) {
                  VATConflictUI.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                  return;
                }
                // temporary
                if (formMeta.helpers.isDeductible && !formValues.taxPeriod) {
                  const firstDeductibleItemIndex = formValues.items.findIndex((item) => item.category && deductibleTaxCategories.includes(item.category.id));

                  return pickerController.api.open({
                    value: formValues.items[firstDeductibleItemIndex].category?.id,
                    onChange: (category, taxPeriod) => {
                      itemsCategoryPickerOnChange(firstDeductibleItemIndex, category, taxPeriod);
                    },
                  });
                }

                formAPI.trigger().then((valid) => {
                  if (formValues.isSuggestedTransactionActive) {
                    flushSync(() => {
                      formAPI.setError(ExpenseFormNames.isSuggestedTransactionActive, {
                        type: 'custom',
                        message: 'expense.form.error.suggestedTransaction',
                      });
                    });
                  }
                  if (valid && !formValues.isSuggestedTransactionActive) {
                    return formAPI.handleSubmit((values) => onSubmit(values as IExpenseFormValuesV2))(e);
                  }

                  const fieldWithError = document.querySelector('.field-with-error');
                  if (fieldWithError) {
                    fieldWithError.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  } else {
                    // we have erros for hidden fields
                    // it means some important data is missing
                    Sentry.captureException(
                      {
                        message: createSentryCustomErrorMsg('expense_errors_on_hidden_fields'),
                      },
                      {
                        extra: {
                          formErrors: JSON.stringify(formAPI.formState.errors),
                          formValues: JSON.stringify(formValues),
                        },
                      },
                    );
                  }
                });
              };

              return (
                <StickyControlsContainer className="px-6">
                  {isWeb && (
                    <>
                      <Button
                        structure="secondary"
                        className="flex-1 !border-primary-100 text-primary-600"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={onClose}
                      >
                        {t('close')}
                      </Button>
                      <Button
                        loading={isSubmitting}
                        tracingEvent={ExpensesEvents.WEBAPP_EXPENSE_CLICKEDSAVE}
                        onClick={runValidationAndSubmit}
                        highlightID="submit-expense"
                        className="flex-1"
                      >
                        {t('save')}
                      </Button>
                    </>
                  )}
                  {isExpert && (
                    <>
                      <Button
                        loading={isSubmitting && isReviewed}
                        disabled={isSubmitting}
                        className="w-full"
                        tracingEvent={ExpensesEvents.WEBAPP_EXPENSE_CLICKEDSAVE}
                        onClick={(e) => {
                          formAPI.setValue('accountantReview.reviewStatus', ReviewStatusEnum.reviewed);
                          runValidationAndSubmit(e);
                        }}
                      >
                        {t('save_and_validate')}
                      </Button>
                      <Button
                        structure="secondary"
                        loading={isSubmitting && !isReviewed}
                        disabled={isSubmitting}
                        className="w-full"
                        tracingEvent={ExpensesEvents.WEBAPP_EXPENSE_CLICKEDSAVE}
                        onClick={(e) => {
                          formAPI.setValue('accountantReview.reviewStatus', ReviewStatusEnum.notReviewd);
                          runValidationAndSubmit(e);
                        }}
                      >
                        {t('save_and_dont_validate')}
                      </Button>
                    </>
                  )}
                </StickyControlsContainer>
              );
            })()}
        </form>
      </FormProvider>
    </>
  );
};

export default Form;
